// src/views/menage/FriseJournee.jsx
//
// LA JOURNÉE EN FRISE — refonte 3 (maquette validée Dimitri, 2026-08-11).
//
// CE QUI CHANGE PAR RAPPORT À LA REFONTE 2, ET POURQUOI :
//
// 1. GROUPÉE PAR URGENCE, PLUS PAR HEURE. « À régler », « Demandes »,
//    « En cours », « À venir », « Validés ». Le fond rosé quittait la ligne pour
//    monter sur le TITRE du groupe : la veille, sept lignes sur huit étaient
//    rosées, et un signal porté par presque toutes les lignes ne signale plus
//    rien. Il y a maintenant UN endroit rosé sur la page.
//
// 2. LES MOTS SONT DANS LA COLONNE DE GAUCHE (126 px), les CHIFFRES dans le
//    bloc. Le nom du logement quitte le bloc : sur deux heures d'axe il n'en
//    restait qu'une lettre. Les noms s'alignent donc verticalement, et la liste
//    des logements se parcourt du regard.
//
// 3. LE BLOC SE REMPLIT. Foncé de son début jusqu'à l'heure courante, ciel
//    ensuite — un dégradé NET, pas un fondu. On lit d'un coup d'œil où en est
//    la journée sans compter les traits de graduation.
//
// 4. LE TEXTE SUIT LE FOND. Le contenu est écrit DEUX FOIS en superposition,
//    découpé au pourcentage exact par `clip-path` : la lettre à cheval sur la
//    frontière change de couleur EN SON MILIEU. Écrire le texte une seule fois
//    obligerait à choisir une couleur qui perd le contraste d'un côté ou de
//    l'autre.
//
// 5. LA DEMANDE D'HORAIRE DESCEND SUR SA PROPRE BANDE. Elle était superposée au
//    bloc : deux informations dans le même rectangle, aucune des deux lisible.
//
// 6. UNE SEULE SIGNIFICATION POUR LA BORDURE. `inset 2px #b8412b` = « c'est
//    dans À RÉGLER ». Nulle part ailleurs. Un ménage en cours n'a aucune
//    bordure : il se déroule normalement.
//
// AUCUN MONTANT ici. Les gestes marchent au DOIGT (pointer events). Les blocs
// créés à la volée réagissent comme les autres : React délègue les événements à
// la racine et les gestionnaires sont des props — rien n'est attaché au
// montage, donc rien n'est à ré-attacher.

import { useState, useMemo, useRef, useCallback, useEffect, useLayoutEffect } from "react"
import PortailFeuille from '../../components/PortailFeuille'
import useVoileEcran from '../../lib/useVoileEcran'
import { DS } from "../../lib/ui"
import { traceSurLaLigne } from "../../lib/menage-trace-derniere-main"
import { recalageAcquitte } from "../../lib/menage-frise"
import { rangsDeListe } from '../../lib/menage-frise'
import { HelpCircle, ChevronRight as ChevronLegende, ChevronDown as ChevronLegendeOuvert } from 'lucide-react'
import CarteBordFrise, {
  FOND_PISTE_FRISE, HACHURE_DEMANDE_FRISE, HACHURE_CONTOUR,
  PLAGE_FOND, PLAGE_BORD, BLOC_MENAGE_FOND, BLOC_VALIDE, BLOC_RETARD,
  AVANCEMENT_FOND, AVANCEMENT_PLEIN, HORLOGE_DEMANDE_FOND, REPERE_CONTRAT_TRAIT,
} from '../../components/CarteBordFrise'
import {
  HEURES_GRADUEES, SANS_AGENT, TYPE_INTERVENTION, ligneAHistoire,
  FRISE_DEBUT_MIN, FRISE_AMPLITUDE_MIN,
  minutesDeHeure,
  pctDeMinutes, largeurPct, minutesDePct, aimanter, bornerDebut,
  formaterHeure, formaterHeureCourte, formaterDuree, libelleHeures, texteDuBloc,
  dureeParDefautDuLogement, avancementPct,
  chargeAgent, libelleCharge, etirerBloc, tracerBloc, estUnClic, etatBloc,
  arriveeAffichee, estDejaArrive, heureDeReference,
  detecterConflitsParAgent, messageConflitAgent, messageAgentsLibres, pointeConflit,
  estNonValide, construireBlocs, grouperParAgent, repartirEnVoies,
} from "../../lib/menage-frise"
import { firstNameFromEmail, libelleDepuisEmail, initialsFromEmail } from "./RunCard"
import useEchapPourFermer from "../../lib/useEchapPourFermer"
import useEtroit from "../../lib/useEtroit"
import {
  AlertTriangle, Clock, UserPlus, User, Home,
  Plus, X, Search, CircleCheck,
} from "lucide-react"
// LE PANNEAU DU MÉNAGE (maquette 2026-08-19) — il REMPLACE l'ancien DetailBloc.
// Le mécanisme d'ouverture (menu → « Détails du ménage ») ne change pas.
import PanneauMenage from "./PanneauMenage"
// LOT FRISE CONTRÔLE — pastille œil sur les blocs contrôlés + bouton
// « Contrôler » dans le détail d'un bloc fini. Touches ADDITIVES uniquement
// (page validée Dimitri) : sans donnée contrôle dans le ctx, RIEN ne change.
import PastilleControle from "../../components/PastilleControle"
// LA BARRE D'ACTIONS du menu du premier clic (maquette 2026-08-19) : Code ·
// Contrôler · Signaler · Détails — la partie basse du MenuAgents vit là-bas.
import BarreActionsBloc from "./BarreActionsBloc"
import BasculeEspace from "../../components/BasculeEspace"

// ── PALETTE — charte 2026. AUCUNE autre couleur. ─────────────────────────────
// Le violet est le SEUL ajout, réservé aux interventions : dès qu'un bloc est
// violet, c'est une intervention.
const C = {
  gris: '#3d5166', fonce: '#2e3d4d', ciel: '#87b8e6',
  cielPale: '#e4f0fb', cielTexte: '#1c2833',
  colonne: '#f4f9fd', bord: '#d5e3ee', blocGris: '#f6f9fc',
  nom: '#233240', muet: '#98a2ad',
  plage: '#eef4f9', plageBord: '#c3d5e4', plageValide: '#f4f6f5',
  // Alertes — trois intensités, une par usage, jamais interchangeables.
  accent: '#c1512d',      // point du titre, texte du bouton d'assignation
  bordAlerte: '#b8412b',  // LA bordure de « à régler », et rien d'autre
  texteAlerte: '#8f2f1d', // les mots d'un problème
  fondAlerte: '#fdf4f0',  // bande de titre, ligne « Personne »
  fondAssigner: '#fdf0ea',// la plage devenue bouton
  // Validé — le vert n'existe QUE là.
  valide: '#dbe9e2', valideTexte: '#1f5a43', valideCoche: '#2c7a5c', fondValide: '#f4f8f5',
  inter: '#7b6bb0',
  maintenant: '#c1512d',
  apresVide: '#f6f9fc', apresVideBord: '#d5e3ee', apresVideTexte: '#98a2ad',
  traitVide: '#c3ccd4',
}

// ⚠️ L'AXE DÉMARRE EXACTEMENT ICI. La même constante sert la colonne de gauche
// de chaque ligne, le décalage de l'en-tête et celui du trait « maintenant » :
// trois valeurs indépendantes finiraient par diverger d'un pixel, et un axe
// décalé d'un pixel fait mentir toutes les heures qu'il gradue.
const LARGEUR_GAUCHE = 126
const HAUTEUR_BLOC = 26
const RAYON = 6
const PASTILLE = 15
const PITCH_VOIE = 30         // empilement des sous-couloirs (vue par agent)

const TEXTE_CLAIR = '#fff'
const TEXTE_SOMBRE = '#1c2833'

// ── Axe ─────────────────────────────────────────────────────────────────────

// ⚠️ HAUTEUR FIXE (2026-08-16) : ancrées top/bottom, les graduations
// s'étiraient avec la piste agrandie du libellé « contrat 11h » — la « série
// de traits sous la piste ». Même bug que les cartes, 3e occurrence.
function Graduations({ hauteur = HAUTEUR_BLOC }) {
  return HEURES_GRADUEES.map((h) => (
    <div key={h} aria-hidden="true"
      style={{position:'absolute',left:`${pctDeMinutes(h * 60)}%`,top:0,height:hauteur,
        width:1,background:C.bord,opacity:.7}} />
  ))
}

function EnTeteAxe() {
  return (
    <div style={{display:'flex'}}>
      <div className="fbz-col-gauche" style={{width:LARGEUR_GAUCHE,flexShrink:0}} />
      <div style={{position:'relative',flex:1,minWidth:0,height:18,marginBottom:2}}>
        {HEURES_GRADUEES.map((h) => (
          <span key={h} style={{position:'absolute',left:`${pctDeMinutes(h * 60)}%`,top:0,
            transform:'translateX(-50%)',fontSize:10,fontWeight:700,color:C.muet,
            fontFamily:DS.fontBody,letterSpacing:'.04em',whiteSpace:'nowrap'}}>{h}h</span>
        ))}
      </div>
    </div>
  )
}

function TraitMaintenant({ nowMin, visible }) {
  if (!visible) return null
  const pct = pctDeMinutes(nowMin)
  if (pct == null) return null
  return (
    <div data-testid="frise-trait-maintenant" aria-hidden="true"
      style={{position:'absolute',left:`${pct}%`,top:0,bottom:0,width:2,
        background:C.maintenant,zIndex:7,pointerEvents:'none'}} />
  )
}




// ── Le disque d'annonce s'explique (survol + clic, 2026-08-16) ─────────────
function DisqueAnnonce({ testId, pct, top }) {
  const [bulle, setBulle] = useState(false)
  const refBulle = useRef(null)
  // FERMETURE STANDARD (tri Dimitri 2026-08-17) : Échap + clic dehors,
  // comme toutes les bulles de la page.
  useEchapPourFermer(() => setBulle(false), bulle)
  useEffect(() => {
    if (!bulle) return undefined
    const surPointerDown = (e) => {
      if (refBulle.current && !refBulle.current.contains(e.target)) setBulle(false)
    }
    const timer = setTimeout(() => document.addEventListener('pointerdown', surPointerDown, true), 0)
    return () => { clearTimeout(timer); document.removeEventListener('pointerdown', surPointerDown, true) }
  }, [bulle])
  return (
    <>
      {/* Cible 28px autour du disque de 13 (vérification 2026-08-17) —
          même logique que l'horloge de demande : le dessin reste petit,
          le doigt a une vraie surface. */}
      <button type="button" data-testid={testId}
        className="fbz-hitbox44"
        title="Heure annoncée par le voyageur"
        aria-label="Heure annoncée par le voyageur"
        onClick={() => setBulle((v) => !v)}
        style={{position:'absolute',left:`${pct}%`,top:top - 7,transform:'translateX(-50%)',
          width:28,height:28,border:'none',background:'none',padding:0,
          display:'inline-flex',alignItems:'center',justifyContent:'center',
          zIndex:3,cursor:'pointer'}}>
        <span aria-hidden="true" style={{width:13,height:13,borderRadius:'50%',
          background:'#7fc4a3',border:'2px solid #1a4f3a',boxSizing:'border-box'}} />
      </button>
      {bulle && (
        <span data-testid={`${testId}-bulle`}
          style={{position:'absolute',left:`${pct}%`,top:top - 22,
            transform:'translateX(-50%)',padding:'3px 8px',borderRadius:6,
            background:'#233240',color:'#fff',fontSize:9.5,whiteSpace:'nowrap',
            fontFamily:DS.fontBody,zIndex:9,pointerEvents:'none'}}>
          heure annoncée par le voyageur
        </span>
      )}
    </>
  )
}

// ── Pastille ────────────────────────────────────────────────────────────────

/**
 * Deux lettres, en majuscules, jamais vides.
 *
 * `initialsFromEmail` dérive déjà le prénom puis ses deux premières lettres
 * (Sofia → SO). On ne relit jamais l'email brut ici : une pastille qui
 * afficherait « SO@ » trahirait qu'on lit l'adresse et non la personne.
 */
function deuxLettres(email) {
  const i = String(initialsFromEmail(email) || '').toUpperCase().slice(0, 2)
  return i.length === 2 ? i : (i + '?').slice(0, 2)
}

// Rond BLANC 15 px — dans les DEUX couches du dégradé, quel que soit le fond.
// ⚠️ SON BLOC DIT « MOI », PAS SES INITIALES (règle Dimitri 2026-08-23) : le
// repère reste — il faut bien distinguer son ménage de ceux des autres — mais
// on ne lui rappelle pas qui il est. Les initiales des AUTRES ne bougent pas :
// elles disent QUI, c'est toute leur utilité.
function PastilleAgent({ email, taille = PASTILLE, moi = false }) {
  return (
    <span aria-hidden="true"
      style={{width:taille,height:taille,borderRadius:'50%',background:'#fff',
        color:TEXTE_SOMBRE,fontSize:moi ? 5.8 : 6.5,fontWeight:700,fontFamily:DS.fontBody,
        letterSpacing:'.02em',display:'inline-flex',alignItems:'center',
        justifyContent:'center',flexShrink:0}}>
      {moi ? 'MOI' : deuxLettres(email)}
    </span>
  )
}

// ── LE CONTENU D'UN BLOC — écrit deux fois, découpé au pourcentage ──────────

/**
 * ⚠️ POURQUOI DEUX COUCHES PLUTÔT QU'UNE COULEUR MOYENNE.
 *
 * Le fond change de couleur à X % de la largeur du bloc. Une lettre posée à
 * cheval sur cette frontière a son flanc gauche sur du #3d5166 et son flanc
 * droit sur du #87b8e6 : aucune couleur d'encre unique ne tient sur les deux.
 * On écrit donc le MÊME contenu deux fois, en superposition exacte, et on
 * découpe chaque couche au pourcentage du fond — la lettre change de couleur
 * exactement là où le fond change, en son milieu s'il le faut.
 *
 * `overflow:hidden` sur le conteneur : sans lui, `clip-path` découpe la boîte
 * mais laisse le débordement d'un texte trop long s'échapper des deux côtés.
 */
function ContenuBloc({ pct, agent, nom, droite, couleurUnique = null, self = null }) {
  const rangee = (couleur) => (
    <span style={{position:'absolute',inset:0,display:'flex',alignItems:'center',gap:4,
      padding:'0 6px',color:couleur,fontFamily:DS.fontBody,boxSizing:'border-box',
      pointerEvents:'none'}}>
      {agent && <PastilleAgent email={agent} moi={!!self && agent === self} />}
      {nom && (
        <span style={{flex:1,minWidth:0,fontSize:8.5,fontWeight:600,overflow:'hidden',
          textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{nom}</span>
      )}
      <span style={{marginLeft:'auto',fontSize:8,fontVariantNumeric:'tabular-nums',
        whiteSpace:'nowrap',flexShrink:0}}>{droite}</span>
    </span>
  )

  // Fond uni (validé, intervention) : une seule couche suffit, et deux couches
  // superposées sur un fond uni ne feraient qu'épaissir le rendu du texte.
  if (couleurUnique) return rangee(couleurUnique)
  if (pct <= 0) return rangee(TEXTE_SOMBRE)
  if (pct >= 100) return rangee(TEXTE_CLAIR)
  return (
    <>
      <span aria-hidden="true" style={{position:'absolute',inset:0,
        clipPath:`inset(0 ${100 - pct}% 0 0)`}}>{rangee(TEXTE_CLAIR)}</span>
      <span style={{position:'absolute',inset:0,
        clipPath:`inset(0 0 0 ${pct}%)`}}>{rangee(TEXTE_SOMBRE)}</span>
    </>
  )
}

/** Poignée : 10 px de large, une barre de 2 × 13 px. */
function Poignee({ bord, testId, handlers, sombre }) {
  return (
    <span data-testid={testId} {...handlers}
      style={{position:'absolute',top:0,bottom:0,[bord === 'gauche' ? 'left' : 'right']:0,
        width:10,cursor:'ew-resize',display:'flex',alignItems:'center',
        justifyContent:'center',touchAction:'none',zIndex:3}}>
      {/* Visibles AU SURVOL seulement (2026-08-16) : en permanence, elles se
          lisaient comme des traits parasites dans le bloc. */}
      <span aria-hidden="true" className="fbz-poignee-barre" style={{width:2,height:13,borderRadius:1,
        background: sombre ? 'rgba(255,255,255,.55)' : 'rgba(28,40,51,.4)',
        opacity:0, transition:'opacity .15s',
        pointerEvents:'none'}} />
    </span>
  )
}

// ── L'apparence d'un bloc ───────────────────────────────────────────────────

// Direction visuelle 2026-08-16 : LE NAVY DISPARAÎT DU BLOC (il appartient au
// voyageur). Quatre états : pas commencé / en cours = ciel plein ; validé =
// vert liseré ; en retard = rosé liseré. L'avancement n'emplit plus le bloc —
// il vit dans une bande de 6 px en bas (voir le rendu).
function apparenceBloc({ etat, aRegler, masque }) {
  // PERSONNELLE MASQUÉE chez l'admin (2026-08-17) : un bloc GRIS NEUTRE —
  // pas la couleur intervention, pas de liseré : sa largeur dit le créneau,
  // le mot « Indisponible » dit le reste, c'est tout.
  if (masque) {
    return { fond: '#aab4bf', couleurUnique: '#fff', ombre: 'none' }
  }
  if (etat === 'intervention') {
    return { fond: C.inter, couleurUnique: '#fff', ombre: aRegler ? `inset 0 0 0 2px ${C.bordAlerte}` : 'none' }
  }
  if (etat === 'valide') {
    return { fond: BLOC_VALIDE.fond, couleurUnique: BLOC_VALIDE.texte,
      ombre: `inset 0 0 0 2px ${BLOC_VALIDE.lisere}` }
  }
  if (etat === 'en_retard') {
    return { fond: BLOC_RETARD.fond, couleurUnique: BLOC_RETARD.texte,
      ombre: `inset 0 0 0 2px ${BLOC_RETARD.lisere}` }
  }
  return {
    fond: BLOC_MENAGE_FOND,
    couleurUnique: '#fff',
    // ⚠️ LA BORDURE N'A QU'UNE SIGNIFICATION : « c'est dans À RÉGLER ».
    ombre: aRegler ? `inset 0 0 0 2px ${C.bordAlerte}` : 'none',
  }
}

// ── Bloc interactif ─────────────────────────────────────────────────────────

function BlocInteractif({
  bloc, etat, aRegler, pct, hauteur, axeRef, top, avecNom, moi,
  onGeste, onMenu, menuOuvert, agentsMenu, onChoisirAgent, onOuvrirDetail,
  // ⚠️ GLISSER VERTICAL — vue « Par agent » SEULEMENT (le couloir « Personne »
  // l'annonçait depuis le début : « glisser vers un agent »). `couloirs` n'est
  // fourni que par CouloirAgent : en vue par logement il est absent et le
  // vertical n'existe pas — un glissement vertical entre LOGEMENTS ne voudrait
  // rien dire. Forme : { monEmail, viser(email|null, {debut,fin,blocId}) }.
  couloirs = null,
  // LOT FRISE CONTRÔLE — props ADDITIVES (défaut absent = rien) : le contrôle
  // du run de ce bloc (pastille œil au coin) et l'ouverture du détail lecture
  // seule au clic sur la pastille.
  controle = null, onOuvrirControle = null,
  // Entrée « Contrôler » du MENU DU PREMIER CLIC (décision Dimitri 2026-08-19,
  // après mesure : le bouton du détail vivait à trois clics de profondeur).
  onControler = null,
  // La barre d'actions du menu (Code / Contrôler / Signaler / Détails) lit le
  // ctx de la frise — additive : sans ctx, ses boutons se grisent, ils ne
  // cassent jamais.
  ctx = null,
}) {
  const dragRef = useRef(null)
  const [apercu, setApercu] = useState(null)

  const debut = apercu ? apercu.debut : bloc.debut
  const fin = apercu ? apercu.fin : bloc.fin
  const ap = apparenceBloc({ etat, aRegler, masque: !!bloc.masque })

  const commencer = useCallback((mode) => (e) => {
    const axe = axeRef?.current
    if (!axe) return
    e.preventDefault()
    e.stopPropagation()
    e.currentTarget.setPointerCapture?.(e.pointerId)
    const rect = axe.getBoundingClientRect()
    dragRef.current = {
      mode, rect, x0: e.clientX, y0: e.clientY,
      debut0: bloc.debut, fin0: bloc.fin,
      offsetMin: minutesDePct(((e.clientX - rect.left) / rect.width) * 100) - bloc.debut,
      bouge: false, aBute: false,
      // Les rects des couloirs, MESURÉS AU DÉPART du geste — pas à chaque
      // mouvement (le layout ne bouge pas pendant un drag : touch-action none
      // bloque le défilement) et jamais devinés depuis un index.
      couloirRects: (couloirs && mode === 'deplacer')
        ? Array.from(document.querySelectorAll('[data-email-couloir]')).map((el) => {
            const r = el.getBoundingClientRect()
            return { email: el.getAttribute('data-email-couloir'), top: r.top, bottom: r.bottom }
          })
        : null,
      couloirCible: null,
    }
  }, [bloc.debut, bloc.fin, axeRef, couloirs])

  const bouger = useCallback((e) => {
    const d = dragRef.current
    if (!d) return
    if (!d.bouge && estUnClic(e.clientX - d.x0, e.clientY - d.y0)) return
    if (!bloc.deplacable) return
    d.bouge = true
    const minute = minutesDePct(((e.clientX - d.rect.left) / d.rect.width) * 100)
    if (d.mode === 'deplacer') {
      const nouveau = bornerDebut(aimanter(minute - d.offsetMin), d.fin0 - d.debut0, bloc.creneau)
      const fin = nouveau + (d.fin0 - d.debut0)
      // ── L'AXE VERTICAL : changer d'agent. Horizontal = changer d'heure,
      // vertical = changer de main. Le couloir sous le doigt devient la cible —
      // SAUF « Personne » : déposer dessus serait DÉSASSIGNER, un geste que la
      // RLS orchestrateur refuse (WITH CHECK cible ∈ pool OU soi) et qu'on ne
      // mime pas côté écran.
      let conflit = false
      if (d.couloirRects && couloirs) {
        const sous = d.couloirRects.find((c) => e.clientY >= c.top && e.clientY <= c.bottom)
        const cible = (sous && sous.email !== couloirs.monEmail && sous.email !== SANS_AGENT)
          ? sous.email : null
        d.couloirCible = cible
        // ⚠️ LE CONFLIT SE VOIT AVANT DE RELÂCHER : le parent le calcule contre
        // les blocs de l'agent VISÉ, à l'heure PROJETÉE. Déposer reste AUTORISÉ
        // (décision Dimitri) : le conflit remonte dans « À régler », et
        // l'orchestrateur réassigne parfois en sachant qu'il ajustera l'heure
        // juste après.
        const v = couloirs.viser(cible, { debut: nouveau, fin, blocId: bloc.id })
        conflit = !!(v && v.conflit)
      }
      setApercu({ debut: nouveau, fin, conflit })
      return
    }
    const r = etirerBloc({
      bord: d.mode, minuteVisee: minute, debut: d.debut0, fin: d.fin0, creneau: bloc.creneau,
    })
    d.aBute = r.aBute
    setApercu({ debut: r.debut, fin: r.fin })
  }, [bloc.creneau, bloc.deplacable])

  const finir = useCallback(() => {
    const d = dragRef.current
    dragRef.current = null
    if (!d) return
    const place = apercu
    setApercu(null)
    // ⚠️ UN SEUL ENDROIT DÉCIDE. Le seuil des 4 px est appliqué dans `bouger`,
    // qui ne lève `bouge` qu'au-delà ; ré-évaluer le seuil ici serait une
    // seconde règle à maintenir, et elle dépendrait de coordonnées que
    // l'environnement ne fournit pas toujours (jsdom n'en porte aucune).
    if (couloirs) couloirs.viser(null)
    if (!d.bouge) { onMenu(bloc.id); return }
    if (place && (place.debut !== d.debut0 || place.fin !== d.fin0)) {
      onGeste(bloc, { debut: place.debut, fin: place.fin, aBute: d.aBute, mode: d.mode })
    }
    // ⚠️ DEUX ÉCRITURES SÉPARÉES, ET C'EST LA BASE QUI L'IMPOSE : le trigger
    // `zzz_enforce_orchestrateur_assign_only` refuse toute réassignation qui
    // toucherait une autre colonne dans le même UPDATE. L'heure part par
    // `onGeste` (mono-colonne), l'agent par `onChoisirAgent` (mono-colonne) —
    // chacune avec son rowcount-check.
    if (d.couloirCible) onChoisirAgent(bloc, d.couloirCible)
  }, [apercu, bloc, onGeste, onMenu, onChoisirAgent, couloirs])

  useEffect(() => () => { dragRef.current = null }, [])

  const handlers = (mode) => ({
    onPointerDown: commencer(mode),
    onPointerMove: bouger,
    onPointerUp: finir,
    onPointerCancel: finir,
  })

  // ⚠️ L'EXCEPTION DE DURÉE PREND LA PLACE DES HEURES (décision Dimitri
  // 2026-08-11) : « 4 h au lieu de 1 h 30 » est plus utile que « 11h - 15h » à
  // ce moment-là. Les horaires restent lisibles à la position du bloc sur
  // l'axe ; l'exception, elle, n'est écrite nulle part ailleurs sur la frise.
  const texte = texteDuBloc({
    debut, fin, dureeParDefaut: bloc.dureeParDefaut, type: bloc.type,
  })
  const droite = etat === 'valide'
    ? <span style={{display:'inline-flex',alignItems:'center',gap:3}}>
        <CircleCheck size={9} strokeWidth={2.6} color={BLOC_VALIDE.texte} /> {texte}
      </span>
    : texte

  const traceBloc = traceSurLaLigne({ run: bloc.run, moi, motif: null })

  return (
    <>
      <div
        data-testid={`frise-bloc-${bloc.id}`}
        role={bloc.deplacable ? 'slider' : undefined}
        // ⚠️ EN VUE « PAR AGENT » IL N'Y A PAS DE COLONNE DE GAUCHE : les
        // couloirs ne portent que des blocs. La ligne de trace n'y tient pas —
        // d'où ce liseré, avec le texte complet en infobulle et la phrase
        // entière au clic dans le détail. Même condition que la ligne.
        title={traceBloc || undefined}
        data-trace={traceBloc ? 'oui' : undefined}
        aria-label={`${bloc.nom} — ${libelleHeures(debut, fin)}${traceBloc ? ` — ${traceBloc}` : ''}`}
        aria-valuetext={formaterHeure(debut)}
        {...(bloc.masque ? {} : handlers('deplacer'))}
        style={{
          position:'absolute', left:`${pctDeMinutes(debut)}%`,
          width:`${largeurPct(debut, fin)}%`,
          top, height: hauteur,
          background: ap.fond,
          borderRadius: RAYON, overflow:'hidden',
          cursor: bloc.masque ? 'default' : bloc.deplacable ? (apercu ? 'grabbing' : 'grab') : 'pointer',
          // ⚠️ LE BLOC GAGNE PARTOUT OÙ IL RECOUVRE (Dimitri 2026-08-16) :
          // au-dessus des hachures (3), des cartes (2) et du disque (3).
          touchAction:'none', zIndex: apercu ? 6 : 4, boxSizing:'border-box',
          // Le conflit se voit PENDANT le geste, pas après le relâchement.
          outline: apercu?.conflit ? `2px solid ${C.texteAlerte}` : undefined,
          outlineOffset: apercu?.conflit ? 1 : undefined,
          // ⚠️ LE LISERÉ DE LA DERNIÈRE MAIN (2026-08-14) + l'ombre d'état :
          // les DEUX coexistent désormais (le doublon de clé les faisait
          // s'écraser en silence).
          boxShadow: [ap.ombre !== 'none' ? ap.ombre : null,
            traceBloc ? `inset 4px 0 0 ${C.gris}` : null].filter(Boolean).join(', ') || undefined,
        }}>
        {bloc.masque ? (
          /* UNIQUEMENT le mot (Dimitri 2026-08-17) : pas de nom d'agent, pas
             de durée écrite, pas d'icône — sa largeur dit déjà le créneau. */
          <span style={{position:'absolute',inset:0,display:'flex',alignItems:'center',
            justifyContent:'center',color:'#fff',fontSize:8.5,fontWeight:600,
            fontFamily:DS.fontBody,pointerEvents:'none'}}>Indisponible</span>
        ) : (
          <ContenuBloc pct={pct} agent={bloc.agent} nom={avecNom ? bloc.nom : null}
            droite={droite} couleurUnique={ap.couleurUnique} self={ctx?.self || null} />
        )}
        {/* L'AVANCEMENT (direction 2026-08-16) : une bande de 6 px en bas du
            bloc — le remplissage s'arrête EXACTEMENT au trait de maintenant.
            Pas de bande sur validé / retard / intervention : leur couleur dit
            déjà tout. */}
        {etat === 'normal' && pct > 0 && (
          <div aria-hidden="true" data-testid={`frise-avancement-${bloc.id}`}
            style={{position:'absolute',left:0,right:0,bottom:0,height:6,
              background:AVANCEMENT_FOND,pointerEvents:'none'}}>
            <div style={{position:'absolute',left:0,top:0,bottom:0,
              width:`${Math.max(0, Math.min(100, pct))}%`,
              background:AVANCEMENT_PLEIN}} />
          </div>
        )}

        {bloc.deplacable && !bloc.masque && (
          <>
            <Poignee bord="gauche" sombre={pct > 12 || etat === 'intervention'}
              testId={`frise-poignee-gauche-${bloc.id}`} handlers={handlers('gauche')} />
            <Poignee bord="droite" sombre={pct > 88 || etat === 'intervention'}
              testId={`frise-poignee-droite-${bloc.id}`} handlers={handlers('droite')} />
          </>
        )}
      </div>

      {/* LOT FRISE CONTRÔLE — la pastille œil, ABSOLUE au coin haut-droit du
          bloc contrôlé : rien du dessin existant ne bouge (le bloc garde son
          overflow, la pastille vit à côté, au-dessus). Cliquer ouvre le détail
          LECTURE SEULE ; le pointerDown est stoppé dans la pastille — jamais
          un glisser démarré sous elle. */}
      {controle && (
        <PastilleControle statut={controle.statut}
          onOuvrir={onOuvrirControle ? () => onOuvrirControle(controle) : undefined}
          style={{position:'absolute',left:`calc(${pctDeMinutes(fin)}% - 12px)`,
            top: top - 7, zIndex: 7}}
          testId={`pastille-controle-${bloc.id}`} />
      )}

      {menuOuvert && (
        <MenuAgents bloc={bloc} left={pctDeMinutes(debut)} top={top + hauteur + 4}
          agentsMenu={agentsMenu} onChoisirAgent={onChoisirAgent}
          onOuvrirDetail={onOuvrirDetail} onFermer={() => onMenu(null)}
          controle={controle} onControler={onControler} ctx={ctx} />
      )}
    </>
  )
}

function MenuAgents({ bloc, left, top, agentsMenu, onChoisirAgent, onOuvrirDetail, onFermer, controle = null, onControler = null, ctx = null }) {
  useVoileEcran()
  // FERMETURE STANDARD (tri Dimitri 2026-08-17) : Échap (hook partagé, pile
  // des modales) ET clic à l'extérieur — le menu ne reste plus collé à
  // l'écran tant qu'on n'a pas choisi quelqu'un.
  useEchapPourFermer(onFermer || (() => {}), !!onFermer)
  // La BARRE D'ACTIONS change la hauteur du menu quand un sous-panneau
  // s'ouvre : ce compteur ré-arme la mesure haut/bas ci-dessous.
  const [taille, setTaille] = useState(0)
  // 🔴 LE MENU S'OUVRAIT TOUJOURS EN DESSOUS, sans jamais regarder s'il y avait
  // la place (bug terrain 2026-08-13, compte orchestrateur). Sur un ménage en
  // bas de frise il sortait de l'écran : le menu existait, était cliquable pour
  // le code, et invisible pour l'humain — donc « assigner ne marche pas ».
  // Pire sur mobile, où la fenêtre est plus courte.
  //
  // ⚠️ ON MESURE, ON NE DEVINE PAS. Un seuil en dur (« si top > 400 ») serait
  // faux dès qu'on change la hauteur de la frise, le zoom ou l'appareil.
  const ref = useRef(null)
  const [versLeHaut, setVersLeHaut] = useState(false)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    // On repart TOUJOURS du placement par défaut avant de mesurer : sinon un
    // menu déjà basculé se mesurerait dans sa position corrigée et oscillerait.
    setVersLeHaut(false)
    const r = el.getBoundingClientRect()
    // ⚠️ LA BONNE LIMITE EST LA SECTION, PAS LA FENÊTRE (bug Dimitri
    // 2026-08-17) : la section est en overflow:hidden — un menu qui dépasse
    // SON bas est rogné même s'il tient dans l'écran. Sur la dernière ligne,
    // il passait « sous la carte ».
    const section = el.closest('[data-testid="frise-journee"]')
    const limiteBas = section ? section.getBoundingClientRect().bottom : window.innerHeight
    const dispoDessous = limiteBas - r.top
    const hauteur = r.height
    // 8 px de marge : un menu qui affleure le bord est aussi pénible qu'un menu
    // coupé — on ne sait pas s'il est entier.
    if (dispoDessous < hauteur + 8 && r.top - hauteur - 8 > 0) setVersLeHaut(true)
  }, [bloc?.id, agentsMenu?.length, taille])

  useEffect(() => {
    if (!onFermer) return undefined
    const surPointerDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onFermer()
    }
    // léger différé : le pointerdown qui OUVRE le menu ne doit pas le fermer.
    const timer = setTimeout(() => document.addEventListener('pointerdown', surPointerDown, true), 0)
    return () => { clearTimeout(timer); document.removeEventListener('pointerdown', surPointerDown, true) }
  }, [onFermer])

  // TÉLÉPHONE (cadrage 2026-08-22) : le popover ancré se faisait rogner au
  // bord — sur étroit, LE MENU MONTE DU BAS, pleine largeur, sous un voile.
  const etroit = useEtroit()
  return (<PortailFeuille actif={etroit}><>
    {etroit && (
      <div data-testid={`frise-menu-voile-${bloc.id}`} onClick={onFermer || undefined}
        style={{position:'fixed',inset:0,background:'rgba(35,50,64,.35)',zIndex:998}} />
    )}
    <div ref={ref} data-testid={`frise-menu-agents-${bloc.id}`}
      data-sens={etroit ? 'feuille' : versLeHaut ? 'haut' : 'bas'}
      style={etroit
        ? {position:'fixed',left:0,right:0,bottom:0,zIndex:999,background:'#fff',
           border:'none',borderRadius:'18px 18px 0 0',
           boxShadow:'0 -8px 30px rgba(35,50,64,.22)',
           display:'flex',flexDirection:'column',maxHeight:'78vh',overflowY:'auto',
           padding:'6px 4px calc(10px + env(safe-area-inset-bottom))'}
        : {position:'absolute',left:`${left}%`,
        top: versLeHaut ? undefined : top,
        bottom: versLeHaut ? `calc(100% - ${top}px + 6px)` : undefined,
        zIndex:40,background:'#fff',border:`1px solid ${C.bord}`,borderRadius:9,
        boxShadow:'0 6px 20px rgba(33,83,135,.18)',overflow:'hidden',
        display:'flex',flexDirection:'column',width:280,maxWidth:280}}>
      {etroit && (<>
        <div aria-hidden="true" style={{width:36,height:4,borderRadius:2,
          background:'#d5e3ee',margin:'4px auto 2px'}} />
        {/* SPEC §7 : le TITRE + la CROIX, puis le CONTEXTE en une ligne. */}
        <div style={{display:'flex',alignItems:'center',gap:8,padding:'4px 12px 0'}}>
          <span style={{flex:1,minWidth:0,fontSize:14.5,fontWeight:700,color:'#233240',
            overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{bloc.nom}</span>
          <button type="button" aria-label="Fermer" onClick={onFermer || undefined}
            className="fbz-press"
            style={{width:32,height:32,borderRadius:9,border:'none',background:'#f4f6f8',
              color:'#5c6773',display:'inline-flex',alignItems:'center',justifyContent:'center',
              cursor:'pointer',flexShrink:0}}>
            <X size={15} strokeWidth={2.2} />
          </button>
        </div>
        <div data-testid={`frise-menu-contexte-${bloc.id}`}
          style={{padding:'2px 12px 4px',fontSize:11,color:'#5c6773',fontFamily:DS.fontBody}}>
          {[firstNameFromEmail(bloc.agent) || 'sans agent',
            `${formaterHeureCourte(bloc.debut)} → ${formaterHeureCourte(bloc.fin)}`,
            estNonValide(bloc, {
              enRetard: bloc.run && ctx?.estEnRetard ? ctx.estEnRetard(bloc.run) : false,
              nowMin: ctx?.nowMin, position: ctx?.positionJour,
            }) ? 'pas validé' : null].filter(Boolean).join(' · ')}
        </div>
        <div style={{padding:'4px 12px 0',fontSize:9.5,fontWeight:700,letterSpacing:'.06em',
          textTransform:'uppercase',color:'#5c7d99'}}>Qui fait ce ménage</div>
      </>)}
      {/* LA RANGÉE D'AGENTS — le geste principal, INCHANGÉE (maquette
          2026-08-19 : seul le bas du menu est remplacé). */}
      <div style={{display:'flex',gap:5,flexWrap:'wrap',padding:7}}>
        {agentsMenu.map((a) => {
          const actuel = a.value === bloc.agent
          return (
            <button key={a.value} type="button"
              data-testid={`frise-agent-${bloc.id}-${a.value}`}
              onClick={(e) => { e.stopPropagation(); onChoisirAgent(bloc, a.value) }}
              style={{display:'inline-flex',alignItems:'center',gap:5,
                border:`1px solid ${actuel ? C.ciel : C.bord}`,
                background: actuel ? C.ciel : '#fff', color: actuel ? C.cielTexte : C.gris,
                borderRadius:7,padding:'5px 9px',fontSize:11,fontWeight:700,cursor:'pointer',
                fontFamily:DS.fontBody,whiteSpace:'nowrap'}}>
              {/* ⚠️ JAMAIS SES PROPRES INITIALES (règle Dimitri 2026-08-23) :
                  pour lui, le libellé est déjà « Moi » — « OR · Moi » répétait
                  une information qu'il connaît. Les initiales des AUTRES
                  restent : elles disent QUI. */}
              {a.isSelf ? a.label : `${initialsFromEmail(a.value)} · ${a.label}`}
            </button>
          )
        })}
      </div>
      {/* LA BARRE D'ACTIONS (maquette 2026-08-19) — elle REMPLACE l'ancien
          bouton « Détails du ménage » ET les entrées/mentions contrôle : les
          mentions grises (« déjà contrôlé », « pas encore contrôlé ») sont
          devenues l'état grisé du bouton « Contrôler », qui continue de dire
          pourquoi (title + aria-label). « Détails » reste le SEUL point
          d'entrée vers le panneau complet. */}
      <BarreActionsBloc bloc={bloc} ctx={ctx} controle={controle}
        onControler={onControler} onOuvrirDetail={onOuvrirDetail}
        onTailleChange={() => setTaille((t) => t + 1)} />
    </div>
  </></PortailFeuille>)
}

// ── LA COLONNE DE GAUCHE — c'est elle qui porte les MOTS ────────────────────

function ColonneGauche({ testId, titre, motif, pastille, opacity = 1, onAcquitter, acquittable, trace }) {
  // L'opacité vit sur un HABIT INTÉRIEUR (correction 22/08) : posée sur la
  // racine, elle rendait transparent le fond blanc sticky du téléphone — les
  // blocs défilaient à travers les noms des lignes validées.
  return (
    <div data-testid={testId} className="fbz-col-gauche"
      style={{width:LARGEUR_GAUCHE,flexShrink:0,minWidth:0,paddingRight:8,
        display:'flex',flexDirection:'column',justifyContent:'center'}}>
      <div style={{display:'flex',flexDirection:'column',justifyContent:'center',gap:1,minWidth:0,opacity}}>
      <span style={{display:'inline-flex',alignItems:'center',gap:5,minWidth:0}}>
        {/* L'ÉTAT SE DIT PAR LA COULEUR, PAS PAR LA PLACE (2026-08-16) : la
            pastille répète la couleur du bloc pour balayer la colonne des
            noms sans regarder la piste. */}
        {pastille && (
          <span aria-hidden="true" data-testid={`${testId}-pastille`}
            style={{width:7,height:7,borderRadius:'50%',background:pastille,flexShrink:0}} />
        )}
        <span className="fbz-nom-frise" style={{fontSize:10.5,color:C.nom,fontWeight:600,overflow:'hidden',
          textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{titre}</span>
      </span>
      {/* LE MOTIF NE S'ÉCRIT QUE QUAND IL Y EN A UN — une journée normale est
          silencieuse (le cas le plus fréquent). Aligné sur le texte du nom. */}
      {motif && (
        <span data-testid={`${testId}-motif`}
          style={{fontSize:9.5,lineHeight:1.35,
            paddingLeft: pastille ? 12 : 0,
            color: motif.ton === 'probleme' ? '#8f2f1d' : '#3d5166',
            overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
          {motif.texte}
        </span>
      )}
      {/* ⚠️ « C'EST BON » N'EXISTE QUE SUR « HEURE RECALÉE ». C'est le seul motif
          qu'aucune action n'éteint : refixer l'heure à la valeur qu'elle a déjà
          serait un geste vide, posé pour faire taire un écran. Les autres se
          règlent en assignant, réassignant ou tranchant — et « non validé » n'en
          a délibérément PAS : l'acquitter reviendrait à déclarer fait un ménage
          que personne n'a confirmé. */}
      {/* 🔴 DÉCOUPLÉ DU MOTIF (décision Dimitri 2026-08-14). Quand un recalage
          CRÉE un conflit, « X sur 2 à la fois » prend la ligne — et le bouton
          disparaissait avec le motif « heure recalée » : l'acquittement était
          inatteignable tant que le conflit durait. Le bouton suit désormais le
          FAIT (un recalage non acquitté existe), pas le motif affiché. */}
      {acquittable && onAcquitter && (
        <button type="button" data-testid={`${testId}-acquitter`}
          onClick={(e) => { e.stopPropagation(); onAcquitter() }}
          className="fbz-press fbz-hitbox44"
          style={{alignSelf:'flex-start',marginTop:2,border:`1px solid ${C.gris}44`,
            background:'transparent',color:C.gris,borderRadius:6,padding:'1px 6px',
            fontSize:8.5,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
          C'est bon
        </button>
      )}
      {/* ⚠️ LA TRACE NE PARAÎT ICI QUE SI AUCUN MOTIF N'OCCUPE LA PLACE — la
          règle `traceSurLaLigne` le garantit. Une information qui n'appelle
          aucune action ne doit pas passer devant « À régler ». */}
      {trace && (
        <span data-testid={`${testId}-trace`} className="fbz-trace-frise"
          style={{fontSize:8,lineHeight:1.3,color:C.gris,opacity:.75,
            overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
          {trace}
        </span>
      )}
      </div>
    </div>
  )
}

// ── UNE LIGNE DE LA VUE PAR LOGEMENT ────────────────────────────────────────

function LigneLogement({ bloc, ctx, enConflit, conflitsParAgent, ouvert, onOuvrir, gestes, menu }) {
  const { guestTimes, idxHoraires, nowMin, positionJour, estEnRetard, checkinTimeByLog } = ctx
  // TÉLÉPHONE (correction colonne 22/08) : l'opacité .6 d'une ligne validée
  // rendait TRANSPARENT le fond blanc de sa colonne sticky — ses blocs
  // apparaissaient à travers les noms. Sur étroit, le voile quitte la LIGNE
  // et s'applique séparément au contenu de la colonne et à la piste ; le fond
  // blanc reste opaque. Ordinateur : voile sur la ligne, inchangé au pixel.
  const etroitLigne = useEtroit()
  const run = bloc.run
  const axeRef = useRef(null)
  const traceRef = useRef(null)
  const [trace, setTrace] = useState(null)

  // RÈGLE DU RETARD (unifiée 2026-08-16) : le bloc finit avant le trait de
  // maintenant sans être validé → EN RETARD — que le check-in soit franchi ou
  // non (deux ménages dans la même situation avaient deux couleurs).
  const enRetard = (run ? estEnRetard(run) : false)
    || (positionJour === 'aujourdhui' && !bloc.termine
        && Number.isFinite(bloc.fin) && Number.isFinite(nowMin) && bloc.fin < nowMin)
  const etat = etatBloc(bloc, { enRetard })
  const creneau = bloc.creneau
  // Le liseré d'alerte du bloc ne dit plus « le groupe » (disparu) : il dit
  // le CONFLIT — la seule urgence que la couleur d'état ne porte pas déjà.
  const aRegler = enConflit
  const hauteurBloc = HAUTEUR_BLOC

  const entree = idxHoraires?.get(`${bloc.logementId}:${run?.scheduled_date}`) || null
  const demande = entree?.demande || null

  // ⚠️ LE CONFLIT EST CELUI DU BLOC, PAS CELUI DE L'AGENT. Karim peut être en
  // conflit sur deux ménages du matin et parfaitement libre sur celui de 15 h 30
  // — écrire « Karim sur 2 à la fois » sur cette troisième ligne l'enverrait
  // chercher un problème qui n'y est pas, et ferait grimper le compteur du
  // bandeau d'autant. On ne prend le motif que si CE bloc-là est dedans.
  // MOTIFS COURTS (2026-08-16) : « pas validé », « sans agent », « N demande(s) »,
  // le conflit garde sa phrase (« Karim sur 2 à la fois ») — RIEN quand la
  // journée se passe bien.
  const motif = useMemo(() => {
    const problemes = []
    if (!bloc.agent && bloc.type !== TYPE_INTERVENTION) problemes.push('sans agent')
    if (enRetard && !bloc.termine) problemes.push('pas validé')
    if (enConflit) {
      const prenom = libelleDepuisEmail(bloc.agent)
      problemes.push(`${prenom} sur 2 à la fois`)
    }
    const doux = demande ? '1 demande' : null
    const texte = [...problemes, ...(doux ? [doux] : [])].join(' · ')
    if (!texte) return null
    return { texte, ton: problemes.length ? 'probleme' : 'demande' }
  }, [bloc, enRetard, enConflit, demande])

  // La pastille : la couleur du bloc, répétée à gauche.
  const pastille = etat === 'valide' ? '#7fc4a3'
    : (etat === 'en_retard' || !bloc.agent || enConflit) ? '#b8412b'
    : bloc.type === TYPE_INTERVENTION ? C.inter
    : '#5c8ab8'

  const pct = avancementPct(bloc, nowMin, positionJour)

  const arrivee = useMemo(() => arriveeAffichee({
    arriveeFormulaire: run ? guestTimes[run.id]?.arrival : null,
    checkinLogement: checkinTimeByLog ? checkinTimeByLog(bloc.logementId) : null,
  }), [run, guestTimes, checkinTimeByLog, bloc.logementId])

  // ── LE LANGAGE DES OCCUPATIONS (report de Demandes, 2026-08-16) — sur les
  // LIGNES À HISTOIRE seulement : annonce, demande en attente, ou conflit
  // (seuil de densité : au-delà de ~7-8 lignes ornées, le gris devient du
  // bruit). Une demande en attente ne contraint RIEN — elle décore ; une
  // annonce est un FAIT — elle place le bord d'occupation.
  const departAnnonceMin = useMemo(() => {
    const brut = run ? guestTimes[run.id]?.departure : null
    return brut ? minutesDeHeure(String(brut).slice(0, 5)) : null
  }, [run, guestTimes])
  const annonceArrivee = arrivee?.source === 'formulaire'
  const histoire = ligneAHistoire({
    demande, annonceArriveeFormulaire: annonceArrivee,
    annonceDepartMin: departAnnonceMin, enConflit,
  })
  // La largeur de la piste en px — pour que la carte de bord sache si son
  // texte tient (règle : jamais hors de sa zone, icône seule sinon).
  const [pisteLargeurPx, setPisteLargeurPx] = useState(0)
  useEffect(() => {
    const el = axeRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => setPisteLargeurPx(el.getBoundingClientRect().width))
    ro.observe(el)
    setPisteLargeurPx(el.getBoundingClientRect().width)
    return () => ro.disconnect()
  }, [])
  // ⚠️ LE BORD DE LA CARTE = L'HEURE RÉELLE DU VOYAGEUR (annonce du
  // formulaire sinon contrat du logement) — JAMAIS le créneau du ménage : un
  // créneau recalé faisait courir la carte jusqu'au bloc (bug 2026-08-16,
  // « je ne vois plus le temps disponible »). Je dois pouvoir lire les
  // horaires par la TAILLE autant que par le texte.
  const checkoutContratMin = useMemo(() => {
    const brut = ctx.checkoutTimeByLog ? ctx.checkoutTimeByLog(bloc.logementId) : null
    return brut ? minutesDeHeure(String(brut).slice(0, 5)) : null
  }, [ctx, bloc.logementId])
  const debutOccupation = Number.isFinite(departAnnonceMin)
    ? departAnnonceMin
    : (Number.isFinite(checkoutContratMin) ? checkoutContratMin : creneau.debut)
  const finOccupation = arrivee && Number.isFinite(arrivee.minutes) ? arrivee.minutes : creneau.fin
  // Une demande PROLONGE le séjour : ses hachures partent de la CARTE DU
  // VOYAGEUR (départ tardif → depuis la carte gauche ; arrivée anticipée →
  // depuis la carte droite), jamais du bloc du ménage.
  const bande = useMemo(() => {
    if (!demande) return null
    const req = demande.requested_time ? minutesDeHeure(String(demande.requested_time).slice(0, 5)) : null
    if (!Number.isFinite(req)) return null
    // B-1 (2026-08-19) : le libellé VOCAL — la synthèse lisait « undefined ».
    const heure = formaterHeure(req)
    const qui = demande.voyageur || 'le voyageur'
    if (demande.event_kind === 'late_checkout') {
      return req > debutOccupation
        ? { debut: debutOccupation, fin: req, late: true,
            libelle: `${qui} veut partir à ${heure}` }
        : null
    }
    return Number.isFinite(finOccupation) && req < finOccupation
      ? { debut: req, fin: finOccupation, late: false,
          libelle: `${qui} veut arriver à ${heure}` }
      : null
  }, [demande, debutOccupation, finOccupation])

  const checkinContratMin = useMemo(() => {
    const brut = checkinTimeByLog ? checkinTimeByLog(bloc.logementId) : null
    return brut ? minutesDeHeure(String(brut).slice(0, 5)) : null
  }, [checkinTimeByLog, bloc.logementId])
  // ⚠️ UNE SEULE CONDITION : l'heure est passée, et la référence est la MÊME
  // pour tous les blocs de l'écran (voir estDejaArrive et heureDeReference).
  const dejaArrive = estDejaArrive(arrivee, heureDeReference(nowMin, positionJour))

  // La piste réserve la place du libellé « contrat 16h » quand le repère
  // existe (sans ça il s'écrasait dans l'écart des lignes — illisible).
  const aRepereContrat = (annonceArrivee && Number.isFinite(arrivee?.minutes))
    || Number.isFinite(departAnnonceMin)
  const hauteurPiste = hauteurBloc + (aRepereContrat ? 16 : 0)

  // TRACER : sur une ligne dont l'heure n'a pas été fixée, clic-glisser dans la
  // plage pose le bloc. Un ménage validé ou sans agent n'est pas traçable —
  // dans le premier cas c'est fait, dans le second il n'y a pas de créneau.
  const traceActive = !!bloc.agent && !bloc.termine && !bloc.aHeureFixee
  const debutTrace = useCallback((e) => {
    if (!traceActive || !axeRef.current) return
    const rect = axeRef.current.getBoundingClientRect()
    const m = minutesDePct(((e.clientX - rect.left) / rect.width) * 100)
    traceRef.current = { rect, p1: m }
    e.currentTarget.setPointerCapture?.(e.pointerId)
    setTrace({ debut: aimanter(m), fin: aimanter(m) })
  }, [traceActive])
  const bougeTrace = useCallback((e) => {
    const t = traceRef.current
    if (!t) return
    const m = minutesDePct(((e.clientX - t.rect.left) / t.rect.width) * 100)
    setTrace(tracerBloc({ point1: t.p1, point2: m, creneau }))
  }, [creneau])
  const finTrace = useCallback(() => {
    const t = traceRef.current
    traceRef.current = null
    if (!t || !trace) { setTrace(null); return }
    gestes.onTracer(bloc, trace)
    setTrace(null)
  }, [trace, bloc, gestes])

  const sansAgent = !bloc.agent

  // ⚠️ LE VOILE DU BANDEAU (2026-08-15) : un filtre actif estompe les lignes
  // non concernées à .2 (transition douce) — un voile visuel, AUCUNE donnée ne
  // change. Sans filtre, le validé garde son .6 historique.
  const voile = ctx.lignesEclairees
    ? (ctx.lignesEclairees.has(bloc.id) ? 1 : 0.2)
    : (etat === 'valide' ? .6 : 1)

  return (
    <div data-testid={`frise-ligne-${bloc.id}`}
      // ⚠️ `opacity` crée un CONTEXTE D'EMPILEMENT : le menu (zIndex 40) reste
      // piégé dans sa ligne et passe SOUS les lignes suivantes (mesuré par
      // sonde le 2026-08-19 — « Détails du ménage » inatteignable dès que la
      // ligne d'en dessous est proche). La ligne au menu ou au panneau OUVERT
      // se surélève.
      // ⚠️ Et l'OPACITÉ S'HÉRITE (mesuré 2026-08-19, panneau « transparent »
      // chez Dimitri) : le .6 d'une ligne validée s'appliquait au menu ET au
      // panneau ouverts dessus — même en position:fixed, un descendant ne
      // peut pas être plus opaque que son ancêtre. Ligne ouverte → opacité 1.
      style={{opacity: etroitLigne ? 1 : ((menu.ouvertId === bloc.id || ouvert) ? 1 : voile),
        transition: 'opacity .22s',
        position: 'relative',
        zIndex: (menu.ouvertId === bloc.id || ouvert) ? 60 : 'auto'}}>
      {/* padding 9px (2026-08-17) : rangée de 44px → les surfaces de contact
          atteignent la cible tactile sans mordre sur la ligne voisine. */}
      <div style={{display:'flex',alignItems:'center',padding:'9px 0'}}>
        <ColonneGauche testId={`frise-gauche-${bloc.id}`} titre={bloc.nom} motif={motif}
          opacity={etroitLigne ? ((menu.ouvertId === bloc.id || ouvert) ? 1 : voile) : 1}
          pastille={pastille}
          trace={traceSurLaLigne({ run: bloc.run, moi: ctx.self, motif })}
          acquittable={!!bloc.heureRecalee && !recalageAcquitte(bloc) && Number.isFinite(bloc.debut)}
          onAcquitter={gestes.onAcquitter ? () => gestes.onAcquitter(bloc) : undefined} />

        <div ref={axeRef} className="fbz-piste-frise"
          style={{position:'relative',flex:1,minWidth:0,height:hauteurPiste,
            opacity: etroitLigne ? ((menu.ouvertId === bloc.id || ouvert) ? 1 : voile) : 1,
            background:FOND_PISTE_FRISE,borderRadius:RAYON}}>
          {etroitLigne && ctx.positionJour === 'aujourdhui' && pctDeMinutes(ctx.nowMin) != null && (
            <span aria-hidden="true" style={{position:'absolute',top:0,bottom:0,width:2,
              left:`${pctDeMinutes(ctx.nowMin)}%`,background:C.maintenant,zIndex:2,
              pointerEvents:'none'}} />
          )}
          {/* Le TRAIT D'ARRIVÉE (filtre « arrivées », 2026-08-15) : posé à
              l'heure d'arrivée de CETTE ligne — on voit d'un coup d'œil si la
              barre du ménage dépasse le trait. */}
          {ctx.marqueursArrivee?.[bloc.id] != null && (
            <span aria-hidden="true" data-testid={`frise-trait-arrivee-${bloc.id}`}
              style={{position:'absolute', left:`${pctDeMinutes(ctx.marqueursArrivee[bloc.id])}%`,
                top:0, height:hauteurBloc, width:2, background:'#5c8ab8', zIndex:3,
                pointerEvents:'none'}} />
          )}
          <Graduations hauteur={hauteurBloc} />

          {/* 0. LE VOYAGEUR — sur TOUTES les lignes (version finale
                2026-08-16) : la carte navy EST son dessin ; ce qui distingue
                une ligne à histoire, ce sont les HACHURES ou le DISQUE
                d'annonce, pas un dessin différent. */}
          {!creneau.sansPlage && (
            <>
              {pctDeMinutes(debutOccupation) > 1 && (
                <CarteBordFrise cote="gauche" echelle="suivi" hauteurPiste={hauteurBloc}
                  pctBord={pctDeMinutes(debutOccupation)}
                  prenom={demande?.event_kind === 'late_checkout' ? demande.voyageur : null}
                  personnes={Number(run?.guest_count) || null}
                  heure={formaterHeureCourte(debutOccupation)}
                  colleInterieur={!!bande && bande.late}
                  largeurZonePx={(pctDeMinutes(debutOccupation) / 100) * pisteLargeurPx}
                  testId={`frise-carte-partant-${bloc.id}`} />
              )}
              {/* LE CONTACT AU CLIC (GO Dimitri 2026-08-17) : une surface
                  transparente SOUS le décidable — z3 comme les hachures, le
                  bloc et « Cliquer pour assigner » (z4) passent DEVANT : la
                  carte ne vole JAMAIS un clic au ménage ni à l'assignation.
                  Rien n'est chargé avant le clic ; la bulle vit dans la vue. */}
              {ctx.onContactVoyageur && run && pctDeMinutes(debutOccupation) > 1 && (
                <button type="button" data-testid={`frise-contact-partant-${bloc.id}`}
                  className="fbz-hitbox44"
                  title="Contacter le voyageur qui part"
                  onClick={(e) => ctx.onContactVoyageur({
                    runId: run.id, cote: 'partant',
                    heure: formaterHeureCourte(debutOccupation),
                    personnes: Number(run?.guest_count) || null,
                    ancre: e.currentTarget.getBoundingClientRect() })}
                  style={{position:'absolute', left:0, top:-9, height:hauteurBloc + 18,
                    width:`${pctDeMinutes(debutOccupation)}%`, zIndex:3,
                    background:'none', border:'none', padding:0, cursor:'pointer'}} />
              )}
              {Number.isFinite(finOccupation) && pctDeMinutes(finOccupation) < 99 && (
                <CarteBordFrise cote="droite" echelle="suivi" hauteurPiste={hauteurBloc}
                  pctBord={pctDeMinutes(finOccupation)}
                  prenom={demande?.event_kind === 'early_checkin' ? demande.voyageur : null}
                  personnes={Number(ctx.guestTimes?.[run?.id]?.arrivant_count) || null}
                  heure={formaterHeureCourte(finOccupation)}
                  colleInterieur={!!bande && !bande.late}
                  alerte={dejaArrive}
                  largeurZonePx={((100 - pctDeMinutes(finOccupation)) / 100) * pisteLargeurPx}
                  testId={dejaArrive ? 'frise-apres-arrive' : `frise-carte-arrivant-${bloc.id}`} />
              )}
              {ctx.onContactVoyageur && run && Number.isFinite(finOccupation) && pctDeMinutes(finOccupation) < 99 && (
                <button type="button" data-testid={`frise-contact-arrivant-${bloc.id}`}
                  title="Contacter le voyageur qui arrive"
                  onClick={(e) => ctx.onContactVoyageur({
                    runId: run.id, cote: 'arrivant',
                    heure: Number.isFinite(finOccupation) ? formaterHeureCourte(finOccupation) : null,
                    personnes: Number(ctx.guestTimes?.[run?.id]?.arrivant_count) || null,
                    ancre: e.currentTarget.getBoundingClientRect() })}
                  style={{position:'absolute', left:`${pctDeMinutes(finOccupation)}%`, top:-9,
                    height:hauteurBloc + 18, width:`${Math.max(0, 100 - pctDeMinutes(finOccupation))}%`, zIndex:3,
                    background:'none', border:'none', padding:0, cursor:'pointer'}} />
              )}
              {/* L'ANNONCE : cercle vert + trait gris du contrat à côté. */}
              {annonceArrivee && Number.isFinite(arrivee?.minutes) && (
                <>
                  {Number.isFinite(checkinContratMin) && checkinContratMin !== arrivee.minutes && (
                    <>
                      {/* Le contrat est une RÉFÉRENCE : il vit SOUS la piste,
                          comme une étiquette d'axe — il ne traverse plus le
                          dessin (correction Dimitri 2026-08-16). */}
                      <div aria-hidden="true" data-testid={`frise-trait-contrat-${bloc.id}`}
                        title="Heure du contrat"
                        style={{position:'absolute',left:`${pctDeMinutes(checkinContratMin)}%`,
                          top:0,height:hauteurBloc,width:2,background:REPERE_CONTRAT_TRAIT,
                          zIndex:2}} />
                      <span data-testid={`frise-contrat-libelle-${bloc.id}`}
                        title="Heure du contrat"
                        style={{position:'absolute',left:`${pctDeMinutes(checkinContratMin)}%`,
                          top:hauteurBloc + 3,transform:'translateX(-50%)',
                          fontSize:8.5,color:'#6b7784',whiteSpace:'nowrap',
                          fontFamily:DS.fontBody}}>
                        contrat {formaterHeureCourte(checkinContratMin)}
                      </span>
                    </>
                  )}
                  <DisqueAnnonce testId={`frise-annonce-cercle-${bloc.id}`}
                    pct={pctDeMinutes(arrivee.minutes)} top={hauteurBloc / 2 - 6} />
                </>
              )}
              {Number.isFinite(departAnnonceMin) && (
                <>
                  <DisqueAnnonce testId={`frise-annonce-cercle-dep-${bloc.id}`}
                    pct={pctDeMinutes(departAnnonceMin)} top={hauteurBloc / 2 - 6} />
                  {Number.isFinite(checkoutContratMin) && checkoutContratMin !== departAnnonceMin && (
                    <>
                      <div aria-hidden="true" data-testid={`frise-trait-contrat-dep-${bloc.id}`}
                        title="Heure du contrat"
                        style={{position:'absolute',left:`${pctDeMinutes(checkoutContratMin)}%`,
                          top:0,height:hauteurBloc,width:2,background:REPERE_CONTRAT_TRAIT,
                          zIndex:2}} />
                      <span data-testid={`frise-contrat-libelle-dep-${bloc.id}`}
                        title="Heure du contrat"
                        style={{position:'absolute',left:`${pctDeMinutes(checkoutContratMin)}%`,
                          top:hauteurBloc + 3,transform:'translateX(-50%)',
                          fontSize:8.5,color:'#6b7784',whiteSpace:'nowrap',
                          fontFamily:DS.fontBody}}>
                        contrat {formaterHeureCourte(checkoutContratMin)}
                      </span>
                    </>
                  )}
                </>
              )}
            </>
          )}

          {/* 1. LA PLAGE — départ du partant → arrivée du suivant.
                 ⚠️ TOUJOURS son fond ET sa bordure : sans la bordure elle se
                 noyait dès que la ligne portait une autre couleur, et
                 l'information de base (combien de temps l'agent a devant lui)
                 disparaissait là où elle est la plus utile. */}
          {!creneau.sansPlage && !sansAgent && (
            <div data-testid={`frise-plage-${bloc.id}`} className="fbz-plage-trace"
              onPointerDown={debutTrace} onPointerMove={bougeTrace}
              onPointerUp={finTrace} onPointerCancel={finTrace}
              style={{position:'absolute',left:`${pctDeMinutes(creneau.debut)}%`,
                width:`${largeurPct(creneau.debut, creneau.fin)}%`,top:0,height:hauteurBloc,
                background: PLAGE_FOND,
                border:`1px solid ${PLAGE_BORD}`,borderRadius:5,boxSizing:'border-box',
                cursor: traceActive ? 'crosshair' : 'default', touchAction:'none'}} />
          )}

          {/* 1bis. SANS AGENT — TOUTE LA PLAGE devient le bouton.
                 ⚠️ Pas de petit bloc pointillé : tant que personne n'est
                 désigné, il n'y a pas de créneau. Dessiner un bloc à une heure
                 que personne n'a décidée ferait passer un défaut pour un choix.
                 La durée à caser est écrite à gauche. */}
          {sansAgent && (
            /* ⚠️ CIBLE 44 px MESURÉE (tri Dimitri 2026-08-17) : l'ancienne
               .fbz-zone-tactile (pseudo-élément ±9 px) NE PRENAIT PAS au
               hit-test — mesuré à ±5 px, le clic tombait sur la ligne. Le
               BOUTON est désormais la hitbox (44 px, transparente) et le
               dessin vit dans un span interne, inchangé à l'œil. */
            <button type="button" data-testid={`frise-assigner-${bloc.id}`}
              onClick={() => menu.basculer(bloc.id)}
              style={{position:'absolute',left:`${pctDeMinutes(creneau.debut)}%`,
                width:`${largeurPct(creneau.debut, creneau.fin)}%`,
                top:-9,height:hauteurBloc + 18,
                background:'none',border:'none',padding:0,cursor:'pointer',
                // ⚠️ TOUT CE QUI SE DÉCIDE passe devant les hachures (règle
                // Dimitri 2026-08-16) : bloc, assignation, poignées.
                zIndex:4}}>
              <span style={{position:'absolute',left:0,right:0,top:9,bottom:9,
                background:C.fondAssigner,boxShadow:`inset 0 0 0 2px ${C.bordAlerte}`,
                borderRadius:RAYON,boxSizing:'border-box',
                display:'flex',alignItems:'center',justifyContent:'center',gap:5,
                fontFamily:DS.fontBody,fontSize:9,fontWeight:700,color:C.accent}}>
                <UserPlus size={11} strokeWidth={2.2} />
                Cliquer pour assigner
              </span>
            </button>
          )}

          {/* 2. LE BLOC DE L'AGENT */}
          {!sansAgent && (
            <BlocInteractif
              bloc={bloc} etat={etat} aRegler={aRegler} pct={pct} hauteur={hauteurBloc}
              axeRef={axeRef} top={0} avecNom={false}
              moi={ctx.self} onGeste={gestes.onGeste} onMenu={menu.basculer}
              menuOuvert={menu.ouvertId === bloc.id}
              agentsMenu={ctx.assignOptions} onChoisirAgent={gestes.onChoisirAgent}
              onOuvrirDetail={onOuvrir}
              controle={run ? (ctx.controleParRun?.[run.id] || null) : null}
              onOuvrirControle={ctx.onOuvrirControle || null}
              onControler={ctx.onControler || null} ctx={ctx} />
          )}

          {sansAgent && menu.ouvertId === bloc.id && (
            <MenuAgents bloc={bloc} left={pctDeMinutes(creneau.debut)} top={hauteurBloc + 4}
              agentsMenu={ctx.assignOptions} onChoisirAgent={gestes.onChoisirAgent}
              onOuvrirDetail={onOuvrir} onFermer={() => menu.basculer(null)}
              controle={run ? (ctx.controleParRun?.[run.id] || null) : null}
              onControler={ctx.onControler || null} ctx={ctx} />
          )}

          {/* 3. LA DEMANDE — SA PROPRE BANDE, sous le bloc, sans aucun texte
                 dedans (9 px ne portent pas de texte). L'heure s'écrit à côté. */}
          {bande && (
            <>
              {/* LA DEMANDE — hachures 45° PLEINE HAUTEUR dans la piste, mêmes
                  teintes que Demandes (unification 2026-08-16 : une même
                  information ne se dessine que d'une seule façon). La zone
                  hachurée EST le lien vers l'écran Demandes ; la pastille
                  horloge du Mois dit l'attente. */}
              <button type="button" data-testid={`frise-demande-${bloc.id}`}
                className="fbz-hitbox44"
                onClick={() => ctx.onOuvrirDemande && ctx.onOuvrirDemande(demande)}
                aria-label={`Demande d'horaire — ${bande.libelle}`}
                style={{position:'absolute',left:`${pctDeMinutes(bande.debut)}%`,
                  width:`${largeurPct(bande.debut, bande.fin)}%`,
                  top:3,height:hauteurBloc - 6,
                  // Collées à la carte : pas d'arrondi ni de contour côté
                  // carte — un seul bloc, moitié plein moitié rayé.
                  borderRadius: bande.late ? '0 4px 4px 0' : '4px 0 0 4px',
                  border:`1px dashed ${HACHURE_CONTOUR}`,
                  ...(bande.late ? { borderLeft:'none' } : { borderRight:'none' }),
                  boxSizing:'border-box',padding:0,
                  cursor: ctx.onOuvrirDemande ? 'pointer' : 'default',
                  background:HACHURE_DEMANDE_FRISE,zIndex:3}} />
              {/* ⚠️ L'HORLOGE EST LA PORTE DE SECOURS (arbitrage Dimitri
                  2026-08-17) : TOUJOURS cliquable, AU-DESSUS du bloc (z5) —
                  si un bloc recouvre un jour 100 % des hachures, la demande
                  reste atteignable ici. Cible élargie à 28px autour du
                  disque de 14 ; elle ne gêne pas le glisser (surface
                  minuscule au bord du bloc). */}
              <button type="button" data-testid={`frise-demande-horloge-${bloc.id}`}
                className="fbz-hitbox44"
                title="Ouvrir la demande d'horaire"
                onClick={(e) => { e.stopPropagation(); ctx.onOuvrirDemande && ctx.onOuvrirDemande(demande) }}
                style={{position:'absolute',
                  left:`calc(${pctDeMinutes(bande.late ? bande.fin : bande.debut)}% - 14px)`,
                  top:hauteurBloc / 2 - 14,
                  width:28,height:28,border:'none',background:'none',padding:0,
                  display:'inline-flex',alignItems:'center',justifyContent:'center',
                  zIndex:5,cursor:'pointer'}}>
                <span aria-hidden="true"
                  style={{width:14,height:14,borderRadius:'50%',background:HORLOGE_DEMANDE_FOND,
                    display:'inline-flex',alignItems:'center',justifyContent:'center'}}>
                  <Clock size={9} strokeWidth={2.6} color="#fff" />
                </span>
              </button>
            </>
          )}

          {/* Tracé en cours */}
          {trace && (
            <div aria-hidden="true" style={{position:'absolute',left:`${pctDeMinutes(trace.debut)}%`,
              width:`${largeurPct(trace.debut, trace.fin)}%`,top:0,height:hauteurBloc,
              borderRadius:RAYON,zIndex:6,background:C.ciel,opacity:.75,display:'flex',
              alignItems:'center',justifyContent:'center',fontSize:8,fontWeight:700,
              color:TEXTE_SOMBRE}}>
              {libelleHeures(trace.debut, trace.fin)}
            </div>
          )}

          {/* 4. CE QUI VIENT APRÈS — un trait à l'heure exacte, une carte à côté. */}
          {/* Le danger « voyageur arrivé pendant un ménage non fini » se dit
              par LA MÊME carte navy, passée en terracotta (un seul objet —
              plus de trait rouge ni de carte doublon, 2026-08-16). */}
        </div>
      </div>

      {/* FERMETURES STANDARD (tri Dimitri 2026-08-17) : Échap + clic dehors,
          comme tout menu/panneau de l'application — sans elles, le panneau
          bloquait tout dessous (attrapé par la pose du 2026-08-19). */}
      {ouvert && <PanneauMenage bloc={bloc} ctx={ctx}
        onFermer={() => onOuvrir(bloc.id)} />}
    </div>
  )
}

// ── VUE PAR AGENT ───────────────────────────────────────────────────────────

function CouloirAgent({ email, blocs, ctx, conflits, conflitsParAgent, gestes, menu, viserCouloir, visee }) {
  const personne = email === SANS_AGENT
  const etroitCouloir = useEtroit()
  const axeRef = useRef(null)
  const { nowMin, positionJour } = ctx
  const charge = useMemo(() => chargeAgent(blocs), [blocs])
  const { blocs: blocsVoies, nbVoies } = useMemo(() => repartirEnVoies(blocs), [blocs])
  const hauteur = Math.max(HAUTEUR_BLOC, (nbVoies - 1) * PITCH_VOIE + HAUTEUR_BLOC)
  const blocsConflit = conflitsParAgent.get(email) || null
  const enConflit = !!blocsConflit
  // LOT PRÉNOMS (2026-08-19) : le prénom SAISI d'abord (ctx.nomAgent lit les
  // options nourries par pool_prenoms), la dérivation en filet.
  const nom = personne ? 'Personne'
    : ((ctx.nomAgent && ctx.nomAgent(email)) || libelleDepuisEmail(email))

  // Le motif d'un agent : son conflit, sinon le premier ménage non validé.
  //
  // ⚠️ ON NE RÉPÈTE PAS SON NOM. Dans la vue par logement le motif dit « Karim
  // sur 2 à la fois » parce que la ligne est un LOGEMENT et qu'il faut nommer
  // celui qui bloque. Ici la ligne EST Karim, son nom est écrit deux
  // centimètres plus haut : le redire consomme la moitié d'une colonne de
  // 126 px pour n'apprendre rien.
  //
  // ⚠️ MÊME RÈGLE DE « NON VALIDÉ » QUE L'AUTRE VUE (`estNonValide`). Elle
  // lisait `estEnRetard` seul, qui n'écoute que le check-in : un ménage dont le
  // créneau était écoulé apparaissait « non validé » côté logement et propre
  // côté agent. Le même fait doit se dire pareil dans les deux onglets, sinon
  // on cherche lequel des deux ment.
  const motif = useMemo(() => {
    if (personne) return { texte: 'glisser vers un agent', ton: 'probleme' }
    if (enConflit) {
      const pointe = pointeConflit(blocsConflit)
      const n = pointe ? pointe.n : 2
      return { texte: `${n} ménages en même temps`, ton: 'probleme' }
    }
    const rate = blocs.find((b) => estNonValide(b, {
      enRetard: b.run ? ctx.estEnRetard(b.run) : false, nowMin, position: positionJour,
    }))
    if (rate) return { texte: `${rate.nom} non validé`, ton: 'probleme' }
    return null
  }, [personne, enConflit, blocsConflit, blocs, ctx, nowMin, positionJour])

  const aProbleme = personne || enConflit || !!motif

  // Le couloir VISÉ par un glissement se signale — fond éclairci, nom en gras.
  // En conflit projeté, il prend le fond d'alerte déjà en usage : aucune
  // couleur nouvelle.
  const estVise = visee?.email === email
  return (
    <div data-testid={`frise-couloir-${email}`}
      data-email-couloir={email}
      data-cible={estVise ? (visee?.conflit ? 'conflit' : 'oui') : undefined}
      style={{display:'flex',alignItems:'stretch',padding:'7px 0',
        background: estVise ? (visee?.conflit ? C.fondAlerte : C.cielPale)
          : (aProbleme ? C.fondAlerte : 'transparent'),
        borderBottom:`1px solid ${C.bord}`}}>
      {/* LA CELLULE (correction 22/08, téléphone) : sur étroit elle vit DANS
          la piste — le compositeur Chrome peint toute la couche défilante
          AU-DESSUS des cellules sticky de ce couloir quel que soit le z-index
          (mesuré : z50, isolation, will-change, ordre DOM sans effet). Dans un
          MÊME contexte (la piste), le tri z redevient fiable : cellule z20 >
          blocs z4/6. L'ordinateur garde la cellule à sa place historique. */}
      {!etroitCouloir && (
      <div className="fbz-col-gauche"
        style={{width:LARGEUR_GAUCHE,flexShrink:0,minWidth:0,
          display:'flex',flexDirection:'column',justifyContent:'center'}}>
      <div role="button" tabIndex={0}
        onClick={() => ctx.onOuvrirRecap && ctx.onOuvrirRecap(email)}
        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && ctx.onOuvrirRecap) { e.preventDefault(); ctx.onOuvrirRecap(email) } }}
        data-testid={`frise-recap-${email}`}
                style={{width:'100%',minWidth:0,padding:'0 8px 0 0',
          background:'transparent',cursor:'pointer',textAlign:'left',fontFamily:DS.fontBody,
          display:'flex',flexDirection:'column',gap:2}}>
        <span style={{display:'flex',alignItems:'center',gap:6,minWidth:0}}>
          {!personne && (
            <span style={{width:20,height:20,borderRadius:'50%',flexShrink:0,
              display:'inline-flex',alignItems:'center',justifyContent:'center',
              fontFamily:DS.fontBody,fontWeight:700,fontSize:7.5,
              background:C.cielPale,color:C.gris}}>{deuxLettres(email)}</span>
          )}
          <span style={{fontSize:10.5,fontWeight: estVise ? 800 : 600,minWidth:0,overflow:'hidden',
            textOverflow:'ellipsis',whiteSpace:'nowrap',
            color: personne ? C.texteAlerte : C.nom}}>{nom}</span>
        </span>
        {/* Sous le nom, aligné sur lui : la charge, puis le motif. */}
        <span style={{paddingLeft: personne ? 0 : 26,display:'flex',flexDirection:'column',gap:1}}>
          {!personne && (
            <span data-testid={`frise-charge-${email}`}
              style={{fontSize:8.5,fontVariantNumeric:'tabular-nums',
                color: charge.depassement ? C.texteAlerte : C.muet,
                fontWeight: charge.depassement ? 700 : 400}}>{libelleCharge(charge)}</span>
          )}
          {motif && (
            <span style={{fontSize:8.5,color: motif.ton === 'probleme' ? C.texteAlerte : C.gris,
              overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{motif.texte}</span>
          )}
        </span>
      </div>
      </div>
      )}

      <div ref={axeRef} className="fbz-piste-frise"
        style={{position:'relative',flex:1,minWidth:0,height:hauteur}}>
        {etroitCouloir && (
          <div className="fbz-col-gauche"
            role="button" tabIndex={0}
            onClick={() => ctx.onOuvrirRecap && ctx.onOuvrirRecap(email)}
            onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && ctx.onOuvrirRecap) { e.preventDefault(); ctx.onOuvrirRecap(email) } }}
            data-testid={`frise-recap-${email}`}
            style={{position:'sticky',left:0,zIndex:20,width:LARGEUR_GAUCHE,height:'100%',
              minWidth:0,padding:'0 8px 0 0',background:'#fff',cursor:'pointer',
              textAlign:'left',fontFamily:DS.fontBody,
              display:'flex',flexDirection:'column',justifyContent:'center',gap:2}}>
        <span style={{display:'flex',alignItems:'center',gap:6,minWidth:0}}>
          {!personne && (
            <span style={{width:20,height:20,borderRadius:'50%',flexShrink:0,
              display:'inline-flex',alignItems:'center',justifyContent:'center',
              fontFamily:DS.fontBody,fontWeight:700,fontSize:7.5,
              background:C.cielPale,color:C.gris}}>{deuxLettres(email)}</span>
          )}
          <span style={{fontSize:10.5,fontWeight: estVise ? 800 : 600,minWidth:0,overflow:'hidden',
            textOverflow:'ellipsis',whiteSpace:'nowrap',
            color: personne ? C.texteAlerte : C.nom}}>{nom}</span>
        </span>
        {/* Sous le nom, aligné sur lui : la charge, puis le motif. */}
        <span style={{paddingLeft: personne ? 0 : 26,display:'flex',flexDirection:'column',gap:1}}>
          {!personne && (
            <span data-testid={`frise-charge-${email}`}
              style={{fontSize:8.5,fontVariantNumeric:'tabular-nums',
                color: charge.depassement ? C.texteAlerte : C.muet,
                fontWeight: charge.depassement ? 700 : 400}}>{libelleCharge(charge)}</span>
          )}
          {motif && (
            <span style={{fontSize:8.5,color: motif.ton === 'probleme' ? C.texteAlerte : C.gris,
              overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{motif.texte}</span>
          )}
        </span>
          </div>
        )}
        {etroitCouloir && ctx.positionJour === 'aujourdhui' && pctDeMinutes(ctx.nowMin) != null && (
          <span aria-hidden="true" style={{position:'absolute',top:0,bottom:0,width:2,
            left:`${pctDeMinutes(ctx.nowMin)}%`,background:C.maintenant,zIndex:2,
            pointerEvents:'none'}} />
        )}
        <Graduations hauteur={hauteur} />
        {/* ⚠️ AUCUNE PLAGE ICI. Un agent n'a pas de fenêtre — c'est le LOGEMENT
            qui en a une. Dessiner la plage du logement derrière le bloc d'un
            agent lui attribuerait une contrainte qui n'est pas la sienne, et
            deux blocs du même agent afficheraient deux plages contradictoires. */}
        {blocsVoies.map((bloc) => {
          // Même règle de retard que la vue par logement (unifiée 2026-08-16).
          const enRetard = (bloc.run ? ctx.estEnRetard(bloc.run) : false)
            || (positionJour === 'aujourdhui' && !bloc.termine
                && Number.isFinite(bloc.fin) && Number.isFinite(nowMin) && bloc.fin < nowMin)
          const etat = etatBloc(bloc, { enRetard })
          // ⚠️ SOUS-COULOIRS : deux blocs qui se chevauchent s'EMPILENT. Se
          // recouvrir donnerait l'alerte sans l'information — on verrait qu'il y
          // a un problème sans pouvoir voir lequel.
          const top = (bloc.voie || 0) * PITCH_VOIE
          const aRegler = personne || conflits.has(bloc.id) || enRetard
          return (
            <BlocInteractif key={bloc.id}
              bloc={bloc} etat={etat} aRegler={aRegler}
              pct={avancementPct(bloc, nowMin, positionJour)}
              hauteur={HAUTEUR_BLOC} axeRef={axeRef} top={top} avecNom
              moi={ctx.self} onGeste={gestes.onGeste} onMenu={menu.basculer}
              menuOuvert={menu.ouvertId === bloc.id}
              couloirs={viserCouloir ? { monEmail: email, viser: viserCouloir } : null}
              agentsMenu={ctx.assignOptions} onChoisirAgent={gestes.onChoisirAgent}
              controle={bloc.run ? (ctx.controleParRun?.[bloc.run.id] || null) : null}
              onOuvrirControle={ctx.onOuvrirControle || null}
              onControler={ctx.onControler || null} ctx={ctx} />
          )
        })}
      </div>
    </div>
  )
}



// ── LA LÉGENDE — repliée par défaut, TOUJOURS (Dimitri 2026-08-17) : elle
// s'apprend une fois, elle n'encombre pas chaque jour. Un échantillon visuel
// à gauche, la phrase à droite — jamais de paragraphe. ──
const LEGENDE_LIGNES = [
  { cle: 'voyageur', phrase: 'La carte navy : le voyageur, son heure, le nombre de personnes',
    ech: { background: '#3d5166', borderRadius: 4 } },
  { cle: 'plage', phrase: 'La plage blanche : le temps disponible pour le ménage',
    ech: { background: '#fff', border: '1px solid #dbe3ea', borderRadius: 4 } },
  { cle: 'menage', phrase: "Le bloc bleu : le ménage prévu ou en cours, avec sa bande d'avancement",
    ech: { background: 'linear-gradient(to top, #233240 0 3px, #5c8ab8 3px 100%)', borderRadius: 4 } },
  { cle: 'valide', phrase: 'Le bloc vert : validé',
    ech: { background: '#7fc4a3', boxShadow: 'inset 0 0 0 2px #1a4f3a', borderRadius: 4 } },
  { cle: 'probleme', phrase: 'Le bloc terracotta : un problème',
    ech: { background: '#f0c4b6', boxShadow: 'inset 0 0 0 2px #b8412b', borderRadius: 4 } },
  { cle: 'demande', phrase: 'Les hachures : un voyageur demande ce temps en plus',
    ech: { background: 'repeating-linear-gradient(45deg, rgba(61,81,102,.5) 0 5px, rgba(61,81,102,.16) 5px 10px)', borderRadius: 4 } },
  { cle: 'annonce', phrase: 'La bulle verte : heure annoncée au formulaire',
    ech: { background: '#7fc4a3', border: '2px solid #1a4f3a', borderRadius: '50%', width: 11, height: 11 } },
  { cle: 'contrat', phrase: "Le trait gris : l'heure du contrat",
    ech: { background: '#8b96a3', width: 2, height: 13 } },
]

function LegendeFrise() {
  const [ouverte, setOuverte] = useState(false)
  return (
    <div data-testid="frise-legende" style={{borderTop:`1px solid ${C.bord}`}}>
      <button type="button" data-testid="frise-legende-bascule"
        onClick={() => setOuverte((v) => !v)}
        className="fbz-press"
        style={{display:'flex',alignItems:'center',gap:6,width:'100%',
          background:'none',border:'none',cursor:'pointer',padding:'8px 12px',
          minHeight:44,
          fontSize:10.5,color:C.muet,fontFamily:DS.fontBody}}>
        <HelpCircle size={12} strokeWidth={2} color={C.muet} />
        Que veulent dire les couleurs ?
        {ouverte
          ? <ChevronLegendeOuvert size={13} strokeWidth={2.2} color={C.muet} />
          : <ChevronLegende size={13} strokeWidth={2.2} color={C.muet} />}
      </button>
      {ouverte && (
        <div data-testid="frise-legende-contenu" style={{padding:'0 12px 10px'}}>
          {LEGENDE_LIGNES.map((l) => (
            <div key={l.cle} style={{display:'flex',alignItems:'center',gap:8,marginBottom:5}}>
              <span aria-hidden="true"
                style={{width:34,height:12,flexShrink:0,boxSizing:'border-box', ...l.ech}} />
              <span style={{fontSize:10.5,color:'#5c6773',fontFamily:DS.fontBody}}>{l.phrase}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Modales (inchangées côté règles, alignées côté palette) ─────────────────

export function ModaleDuree({ bloc, logement, onFermer, onValider }) {
  useVoileEcran()
  const etroitDuree = useEtroit() // feuille du bas (cadrage 2026-08-22)
  // Échap ferme — consigne 2026-08-14, hook partagé (pile anti-double-fermeture).
  useEchapPourFermer(onFermer)
  const parDefaut = dureeParDefautDuLogement(logement)
  const [minutes, setMinutes] = useState(bloc.dureeMin)
  const [aussiParDefaut, setAussiParDefaut] = useState(false)
  const exception = minutes !== parDefaut

  return (
    <PortailFeuille actif={etroitDuree}>
    <div role="dialog" aria-label="Durée du ménage" data-testid="frise-modale-duree"
      style={{position:'fixed',inset:0,background:'rgba(46,61,77,.42)',zIndex:60,
        display:'flex',alignItems: etroitDuree ? 'flex-end' : 'center',justifyContent:'center',
        padding: etroitDuree ? 0 : 16}}>
      <div style={{background:'#fff',borderRadius: etroitDuree ? '18px 18px 0 0' : 14,
        padding:'18px 20px',maxWidth: etroitDuree ? 'none' : 400,width:'100%',
        display:'flex',flexDirection:'column',gap:13,fontFamily:DS.fontBody}}>
        <div style={{display:'flex',alignItems:'flex-start',gap:10}}>
          <div style={{flex:1}}>
            <div style={{fontFamily:DS.fontDisplay,fontSize:16,fontWeight:600,color:C.fonce}}>
              Durée du ménage
            </div>
            <div style={{fontSize:12,color:DS.textSecondary,marginTop:2}}>{bloc.nom}</div>
          </div>
          <button type="button" onClick={onFermer} aria-label="Fermer"
            style={{background:'none',border:'none',cursor:'pointer',color:C.muet,padding:2}}>
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        <label style={{display:'flex',flexDirection:'column',gap:4,fontSize:11.5,
          color:DS.textSecondary}}>
          Durée (minutes)
          <input type="number" min={30} max={720} step={15} value={minutes}
            onChange={(e) => setMinutes(Number(e.target.value))}
            data-testid="frise-duree-minutes" style={champ()} />
          <span style={{color:C.muet}}>soit {formaterDuree(minutes)}</span>
        </label>

        {exception && !aussiParDefaut && (
          <div data-testid="frise-duree-rappel"
            style={{background:C.blocGris,borderRadius:9,padding:'9px 12px',fontSize:11.5,
              color:DS.textSecondary,lineHeight:1.5}}>
            Exception pour ce ménage-là seulement — les prochains ménages de ce
            logement repartiront à {formaterDuree(parDefaut)}.
          </div>
        )}

        <label style={{display:'flex',alignItems:'flex-start',gap:8,fontSize:12,color:C.fonce,
          cursor:'pointer'}}>
          <input type="checkbox" checked={aussiParDefaut} data-testid="frise-duree-par-defaut"
            onChange={(e) => setAussiParDefaut(e.target.checked)}
            style={{marginTop:2,flexShrink:0}} />
          <span>
            {/* Le NOM du logement, jamais « ce logement » (Dimitri 2026-08-19) :
                on doit savoir ce qu'on change. */}
            Faire de {formaterDuree(minutes)} la durée <strong>par défaut</strong> de {logement?.nom || bloc.nom}
            <span style={{display:'block',color:C.muet,fontSize:11,marginTop:2}}>
              actuellement {formaterDuree(parDefaut)} · vaudra pour les prochains ménages
            </span>
          </span>
        </label>

        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <button type="button" onClick={onFermer} className="fbz-press"
            style={boutonSecondaire()}>Annuler</button>
          <button type="button" className="fbz-press"
            onClick={() => onValider({ minutes, aussiParDefaut })}
            style={boutonPlein(C.gris)}>Enregistrer</button>
        </div>
      </div>
    </div>
    </PortailFeuille>
  )
}

export function ModaleIntervention({ logements, agents, onFermer, onValider, avecPersonnel = false }) {
  // TÉLÉPHONE (mécanisme général 22/08) : la modale passe par le portail.
  const etroitInterv = useEtroit()
  useVoileEcran()
  // Échap ferme — consigne 2026-08-14, hook partagé (pile anti-double-fermeture).
  useEchapPourFermer(onFermer)
  const [libelle, setLibelle] = useState('')
  const [logementId, setLogementId] = useState(logements[0]?.id || '')
  const [agent, setAgent] = useState(agents[0]?.value || '')
  const [de, setDe] = useState('08:30')
  const [a, setA] = useState('10:15')
  const [personnel, setPersonnel] = useState(false)

  const duree = useMemo(() => {
    const [h1, m1] = String(de).split(':').map(Number)
    const [h2, m2] = String(a).split(':').map(Number)
    if ([h1, m1, h2, m2].some((n) => !Number.isFinite(n))) return null
    const d = (h2 * 60 + m2) - (h1 * 60 + m1)
    return d > 0 ? d : null
  }, [de, a])
  const pret = libelle.trim().length > 0 && !!agent && duree != null

  return (
    <PortailFeuille actif={etroitInterv}>
    <div role="dialog" aria-label="Nouvelle intervention" data-testid="frise-modale-intervention"
      style={{position:'fixed',inset:0,background:'rgba(46,61,77,.42)',zIndex:60,
        display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'#fff',borderRadius:14,padding:'18px 20px',maxWidth:420,width:'100%',
        display:'flex',flexDirection:'column',gap:13,fontFamily:DS.fontBody}}>
        <div style={{display:'flex',alignItems:'flex-start',gap:10}}>
          <div style={{flex:1}}>
            <div style={{fontFamily:DS.fontDisplay,fontSize:16,fontWeight:600,color:C.fonce}}>
              Nouvelle intervention
            </div>
            <div style={{fontSize:12,color:DS.textSecondary,marginTop:2}}>
              Une tâche qui ne vient d'aucune réservation. Elle peut se poser à
              n'importe quelle heure — aucune plage ne la borne.
            </div>
          </div>
          <button type="button" onClick={onFermer} aria-label="Fermer"
            style={{background:'none',border:'none',cursor:'pointer',color:C.muet,padding:2}}>
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        <label style={{display:'flex',flexDirection:'column',gap:4,fontSize:11.5,
          color:DS.textSecondary}}>
          Ce qu'il y a à faire
          <input value={libelle} onChange={(e) => setLibelle(e.target.value)}
            placeholder="Réception d'une livraison" style={champ()} />
        </label>

        {/* PERSONNEL SANS LOGEMENT (Dimitri 2026-08-17) : la case cochée fait
            DISPARAÎTRE le choix du logement — l'intervention n'occupe que le
            temps de l'agent, rien n'est conservé en base. */}
        {!personnel && (
          <label style={{display:'flex',flexDirection:'column',gap:4,fontSize:11.5,
            color:DS.textSecondary}}>
            Logement
            <select value={logementId} onChange={(e) => setLogementId(e.target.value)} style={champ()}>
              {logements.map((l) => <option key={l.id} value={l.id}>{l.nom}</option>)}
            </select>
          </label>
        )}

        <label style={{display:'flex',flexDirection:'column',gap:4,fontSize:11.5,
          color:DS.textSecondary}}>
          Qui s'en occupe
          <select value={agent} onChange={(e) => setAgent(e.target.value)} style={champ()}>
            {agents.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
          </select>
        </label>

        <div style={{display:'flex',gap:10,alignItems:'flex-end',flexWrap:'wrap'}}>
          <label style={{display:'flex',flexDirection:'column',gap:4,fontSize:11.5,
            color:DS.textSecondary}}>
            De
            <input type="time" value={de} step={300} data-testid="frise-inter-de"
              onChange={(e) => setDe(e.target.value)} style={{...champ(), width:120}} />
          </label>
          <label style={{display:'flex',flexDirection:'column',gap:4,fontSize:11.5,
            color:DS.textSecondary}}>
            À
            <input type="time" value={a} step={300} data-testid="frise-inter-a"
              onChange={(e) => setA(e.target.value)} style={{...champ(), width:120}} />
          </label>
          <span data-testid="frise-inter-duree"
            style={{fontSize:12,fontWeight:700,color: duree ? C.gris : C.texteAlerte,
              paddingBottom:9}}>
            {duree ? `soit ${formaterDuree(duree)}` : 'fin avant le début'}
          </span>
        </div>

        {/* PERSONNEL (arbitrage Dimitri 2026-08-17) : visible ORCHESTRATEUR
            SEUL — l'admin se la cocher reviendrait à se cacher des choses à
            lui-même. Chez l'admin, une personnelle devient un bloc gris
            « Indisponible » : le contenu est blanchi CÔTÉ SERVEUR (vue). */}
        {avecPersonnel && (
          <label data-testid="frise-inter-personnel"
            style={{display:'flex',alignItems:'flex-start',gap:8,fontSize:11.5,
              color:DS.textSecondary,cursor:'pointer'}}>
            <input type="checkbox" checked={personnel}
              onChange={(e) => setPersonnel(e.target.checked)}
              style={{marginTop:2}} />
            <span>
              <span style={{fontWeight:700,color:C.fonce}}>Personnel</span>
              <span style={{display:'block',marginTop:1}}>
                L'administrateur verra le créneau occupé (« Indisponible »),
                sans le libellé ni le logement.
              </span>
            </span>
          </label>
        )}

        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <button type="button" onClick={onFermer} className="fbz-press"
            style={boutonSecondaire()}>Annuler</button>
          <button type="button" disabled={!pret} className="fbz-press"
            // ⚠️ `assigned_provider_email` — le nom que `creerIntervention`
            // déstructure et que la table porte. Le payload envoyait
            // `agent_email` : l'agent choisi ici était silencieusement perdu,
            // et l'intervention naissait sans personne pour la faire.
            onClick={() => onValider({
              libelle: libelle.trim(),
              logement_id: avecPersonnel && personnel ? null : logementId,
              assigned_provider_email: agent, duree_min: duree, heure_fixee: de,
              personnel: avecPersonnel && personnel,
            })}
            style={{...boutonPlein(C.inter), opacity: pret ? 1 : .5,
              cursor: pret ? 'pointer' : 'not-allowed'}}>
            <Plus size={14} strokeWidth={2} /> Ajouter
          </button>
        </div>
      </div>
    </div>
    </PortailFeuille>
  )
}

function champ() {
  return {
    border:`1px solid ${C.bord}`,borderRadius:8,padding:'8px 10px',fontSize:12.5,
    fontFamily:DS.fontBody,color:C.fonce,background:'#fff',width:'100%',boxSizing:'border-box',
  }
}
function boutonPlein(fond) {
  return {
    display:'inline-flex',alignItems:'center',justifyContent:'center',gap:6,background:fond,
    color:'#fff',border:'none',borderRadius:DS.radiusControl,padding:'9px 14px',fontSize:12,
    fontWeight:700,cursor:'pointer',fontFamily:DS.fontBody,
  }
}
function boutonSecondaire() {
  return {
    padding:'9px 14px',borderRadius:DS.radiusControl,border:`1px solid ${C.bord}`,
    background:'#fff',color:C.gris,fontSize:12,fontWeight:700,cursor:'pointer',
    fontFamily:DS.fontBody,
  }
}

const MARGE_LISTE = 12

// ⚠️ SUR MOBILE, LA FRISE DÉFILE — elle ne se comprime pas (décision Dimitri
// 2026-08-11). À 430 px, une fois les 126 px de la colonne de gauche retirés,
// il reste ~280 px pour dix heures d'axe : un ménage d'1 h 30 y fait 42 px, de
// quoi loger la pastille et rien d'autre. Les heures disparaissaient, et un
// bloc sans heures ne se déplace pas au doigt — on ne sait pas vers quoi viser.
//
// ⚠️ LE DÉFILEMENT EST SOUS MEDIA QUERY, jamais permanent : `overflow-x:auto`
// force le navigateur à calculer `overflow-y` en `auto` lui aussi, ce qui
// rognerait le menu des agents qui s'ouvre SOUS un bloc. Au-delà de 860 px la
// place ne manque pas, donc le conteneur reste en flux normal et le menu
// déborde librement, comme aujourd'hui.
const LARGEUR_MIN_PISTE = 720
// ── TÉLÉPHONE (cadrage Dimitri 2026-08-22 : « on la garde, on la libère ») ──
// · le PAN AU DOIGT était mort sur la zone des lignes : la PLAGE porte
//   touch-action:none pour le tracé — sur étroit elle repasse en pan-x (le
//   doigt fait défiler ; fixer l'heure d'un bloc sans heure reste possible en
//   GLISSANT LE BLOC lui-même — le tracé à main levée redevient souris seule) ;
// · la COLONNE DES NOMS devient sticky : les noms restent pendant le pan,
//   et s'écrivent sur DEUX lignes au lieu d'être tronqués ;
// · les BLOCS gardent touch-action:none : le doigt posé sur un bloc le
//   DÉPLACE (aimanté 30 min), le doigt posé ailleurs FAIT DÉFILER.
const CSS_FRISE = `
@media (max-width: 860px){
  .fbz-frise-scroll{overflow-x:auto;-webkit-overflow-scrolling:touch}
  .fbz-frise-piste{min-width:${LARGEUR_MIN_PISTE}px}
  .fbz-plage-trace{touch-action:pan-x !important;cursor:default !important}
  /* LA COLONNE DES NOMS = UN SEUL BLOC BLANC CONTINU (correction Dimitri
     22/08). Trois causes mesurées, trois remèdes — téléphone seulement :
     · les cellules collées au contenu laissaient les paddings des lignes
       transparents (la piste défilait dans les interstices) → chaque cellule
       s'ÉTIRE sur la hauteur de sa ligne (align-self:stretch) et des bandes
       blanches solides (box-shadow haut/bas, à la taille exacte du padding)
       recouvrent les interstices EN SUIVANT la cellule sticky ;
     · le trait « maintenant » (z7) passait AU-DESSUS de la colonne (z6) →
       la colonne monte à z8, tout ce qui défile glisse DESSOUS ;
     · la GOUTTIÈRE mesurée : le scroller a 12 px de padding gauche, la
       cellule se cale au bord INTERNE — le contenu défilant restait visible
       dans la bande [bord..cellule] → bandes blanches à gauche (-14px) ;
     · les séparateurs de lignes restent visibles : les bandes s'arrêtent au
       padding, le filet du bas de ligne n'est jamais recouvert. */
  .fbz-col-gauche{position:sticky;left:0;z-index:30;background:#fff !important;
    align-self:stretch}
  [data-testid^="frise-ligne-"] .fbz-col-gauche{
    box-shadow:0 -10px 0 0 #fff, 0 10px 0 0 #fff,
      -14px 0 0 0 #fff, -14px -10px 0 0 #fff, -14px 10px 0 0 #fff}
  [data-testid^="frise-couloir-"] .fbz-col-gauche{
    box-shadow:0 -8px 0 0 #fff, 0 8px 0 0 #fff,
      -14px 0 0 0 #fff, -14px -8px 0 0 #fff, -14px 8px 0 0 #fff}
  [data-testid^="frise-ligne-"], [data-testid^="frise-couloir-"]{isolation:isolate}
  .fbz-piste-frise{isolation:isolate}
  /* UN VOILE EST OUVERT (feuille/panneau) — LE MÉCANISME GÉNÉRAL (22/08) :
     le compositeur peint les couches du DÉFILEMENT COMPOSITÉ de la frise
     au-dessus de tout voile fixed, quel que soit le z (mesuré : le voile
     gagne le hit-test et perd la peinture). On neutralise LA SOURCE :
     · le scroller perd son overflow composité le temps de la feuille ;
     · les cellules sticky se dé-épinglent (styles inline battus).
     À la fermeture, tout revient — y compris la position de défilement. */
  body.fbz-voile-ouvert .fbz-frise-scroll{overflow-x:hidden !important}
  body.fbz-voile-ouvert .fbz-col-gauche{position:static !important;z-index:auto !important}
  .fbz-col-gauche .fbz-nom-frise{white-space:normal !important;display:-webkit-box;
    -webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
  .fbz-trace-frise{white-space:normal !important;display:-webkit-box;
    -webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
}
`

export default function FriseJournee({
  runs, interventions, logementsById, horaires, heuresFixees, agentsPool = [],
  ctx, vue, onVue, gestes,
}) {
  const [ouvertId, setOuvertId] = useState(null)
  const [menuId, setMenuId] = useState(null)
  const [filtre, setFiltre] = useState('')
  // TÉLÉPHONE : le bloc des conflits ouvre SA feuille (spec §6).
  const etroitFrise = useEtroit()
  const [conflitsOuverts, setConflitsOuverts] = useState(false)
  // TÉLÉPHONE : ouvrir la frise CENTRÉE sur maintenant (sinon on arrive sur
  // 9h et le présent vit hors champ). Sans débordement, no-op.
  const scrollerRef = useRef(null)
  useLayoutEffect(() => {
    const el = scrollerRef.current
    if (!el || el.scrollWidth <= el.clientWidth + 4) return
    const now = ctx?.nowMin
    if (!Number.isFinite(now)) return
    const pct = (now - FRISE_DEBUT_MIN) / FRISE_AMPLITUDE_MIN
    const cible = LARGEUR_GAUCHE + Math.max(0, Math.min(1, pct)) * (el.scrollWidth - LARGEUR_GAUCHE)
    el.scrollLeft = Math.max(0, cible - el.clientWidth * 0.45)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx?.positionJour])

  // ⚠️ LE 3e CONSTRUCTEUR DE BLOCS (bug « bloqué au contrat », 3e signalement
  // Dimitri 2026-08-16) : la frise reconstruisait SES blocs SANS les annonces
  // — les deux memos de la vue étaient corrigés, celui-ci non. La borne du
  // glisser vient d'ICI (bloc.creneau) : elle suit désormais le RÉEL.
  const blocs = useMemo(
    () => construireBlocs({ runs, interventions, logementsById, horaires, heuresFixees,
      guestTimes: ctx.guestTimes || {} }),
    [runs, interventions, logementsById, horaires, heuresFixees, ctx.guestTimes]
  )

  // CONFLITS calculés UNE FOIS, sur TOUS les blocs, groupés par agent — donc
  // identiques dans les deux vues, et recalculés à chaque geste puisque `blocs`
  // dérive des données, qui changent à chaque écriture.
  const { ids: conflits, parAgent: conflitsParAgent } = useMemo(
    () => detecterConflitsParAgent(blocs), [blocs]
  )
  const prenomDe = useCallback((e) => firstNameFromEmail(e) || e, [])
  const messageConflit = useMemo(
    () => messageConflitAgent(conflitsParAgent, prenomDe), [conflitsParAgent, prenomDe]
  )
  // La moitié utile du message : vers QUI se tourner. Les agents déjà en
  // conflit sont exclus — les proposer déplacerait le problème.
  const messageLibres = useMemo(() => {
    if (!messageConflit) return null
    return messageAgentsLibres(blocs, agentsPool, {
      exclure: new Set(conflitsParAgent.keys()), prenomDe,
    })
  }, [messageConflit, blocs, agentsPool, conflitsParAgent, prenomDe])

  const demandeDe = useCallback((b) => {
    const cle = `${b.logementId}:${b.run?.scheduled_date}`
    return ctx.idxHoraires?.get(cle)?.demande || null
  }, [ctx.idxHoraires])

  const filtreNorm = filtre.trim().toLowerCase()
  const blocsFiltres = useMemo(() => (
    filtreNorm ? blocs.filter((b) => String(b.nom).toLowerCase().includes(filtreNorm)) : blocs
  ), [blocs, filtreNorm])

  // ⚠️ UNE SEULE LISTE, ORDRE IMMUABLE (Dimitri 2026-08-16) : l'heure de
  // DÉPART DU CONTRAT — le seul fait qui ne change jamais pendant le travail
  // (l'heure du ménage bouge au glisser, le contrat non) — puis le nom. Sans
  // départ connu (intervention, logement sans horaires) : fin de liste. Une
  // demande, un retard, une validation NE DÉPLACENT JAMAIS une ligne.
  // L'ORDRE (Dimitri 2026-08-17) : 1. ROUGES (avec ou sans demande — le
  // rouge l'emporte toujours, c'est le plus urgent) · 2. BLEUS qui portent
  // une DEMANDE · 3. BLEUS sans demande · 4. VERTS. L'habituel (départ du
  // contrat, nom) dans chaque étage. ⚠️ FIGÉ AU CHARGEMENT : valider un
  // ménage le laisse À SA PLACE, un passage en retard change sa couleur sans
  // le faire remonter. Recalcul au changement de jour ou rechargement seuls.
  const ordreFige = useRef({ cleJour: null, rangs: null })
  const lignes = useMemo(() => {
    // Une intervention SANS logement (personnelle) n'a pas de ligne ici :
    // « en vue par logement, elle ne s'affiche nulle part ; en vue par
    // agent, elle occupe son couloir » (Dimitri 2026-08-17). Le détecteur
    // de conflits la compte toujours (il tourne sur TOUS les blocs).
    const cleDe = (b) => {
      const brut = ctx.checkoutTimeByLog ? ctx.checkoutTimeByLog(b.logementId) : null
      const min = brut ? minutesDeHeure(String(brut).slice(0, 5)) : null
      return [Number.isFinite(min) ? min : 9999, String(b.nom || '')]
    }
    const etage = (b) => {
      const enRetard = (b.run ? ctx.estEnRetard(b.run) : false)
        || (ctx.positionJour === 'aujourdhui' && !b.termine
            && Number.isFinite(b.fin) && Number.isFinite(ctx.nowMin) && b.fin < ctx.nowMin)
      const rouge = (!b.agent && b.type !== TYPE_INTERVENTION) || enRetard || conflits.has(b.id)
      if (rouge && !b.termine) return 0
      if (b.termine) return 3
      return demandeDe(b) ? 1 : 2
    }
    const cleJour = blocs[0]?.run?.scheduled_date || 'vide'
    if (ordreFige.current.cleJour !== cleJour) {
      ordreFige.current = { cleJour, rangs: rangsDeListe({ blocs, etage, cleDe }) }
    } else {
      // les lignes inconnues s'ajoutent en fin — les connues NE BOUGENT PAS.
      ordreFige.current.rangs = rangsDeListe({ blocs, etage, cleDe, rangs: ordreFige.current.rangs })
    }
    const rangs = ordreFige.current.rangs
    return blocsFiltres
      .filter((b) => !(b.type === TYPE_INTERVENTION && !b.logementId))
      .sort((a, b) => (rangs.get(a.id) ?? 999) - (rangs.get(b.id) ?? 999))
  }, [blocs, blocsFiltres, conflits, ctx, demandeDe])

  const parAgent = useMemo(() => grouperParAgent(blocs, agentsPool), [blocs, agentsPool])

  // ── VISÉE DU GLISSER VERTICAL ──────────────────────────────────────────────
  // Le couloir sous le doigt, et le conflit PROJETÉ : les blocs de l'agent visé
  // contre l'intervalle où le bloc atterrirait. Semi-ouvert, comme partout —
  // finir à 13:30 et commencer à 13:30, c'est s'enchaîner, pas se chevaucher.
  const [visee, setVisee] = useState(null)
  const viseeRef = useRef(null)
  const viserCouloir = useCallback((email, projection) => {
    if (!email) {
      if (viseeRef.current !== null) { viseeRef.current = null; setVisee(null) }
      return null
    }
    // ⚠️ `grouperParAgent` rend un TABLEAU de paires [email, blocs], PAS une
    // Map — `.get()` levait un TypeError à chaque pointermove, avalé par le
    // navigateur : la visée s'affichait jamais, le dépôt marchait quand même
    // (posé avant l'appel). Mesuré par instrumentation le 2026-08-13.
    const liste = (parAgent.find(([e]) => e === email) || [])[1] || []
    const conflit = !!projection && liste.some((b) =>
      b.id !== projection.blocId
      && Number.isFinite(b.debut) && Number.isFinite(b.fin)
      && b.debut < projection.fin && projection.debut < b.fin)
    const prochain = { email, conflit }
    // On ne re-rend que si la visée CHANGE — un pointermove arrive par dizaines
    // par seconde, re-rendre à chaque fois ferait ramer le glissement au doigt.
    if (viseeRef.current?.email !== email || viseeRef.current?.conflit !== conflit) {
      viseeRef.current = prochain
      setVisee(prochain)
    }
    return prochain
  }, [parAgent])

  // ⚠️ LES AGENTS À PROBLÈME REMONTENT. Un couloir en conflit trois lignes plus
  // bas se découvre en défilant, c'est-à-dire trop tard.
  const agentsOrdonnes = useMemo(() => {
    const rang = ([email, list]) => {
      if (email === SANS_AGENT) return 2          // la file d'attente reste en bas
      if (conflitsParAgent.has(email)) return 0
      if (list.some((b) => b.run && ctx.estEnRetard(b.run))) return 0
      return 1
    }
    const l = filtreNorm
      ? parAgent.filter(([email, list]) =>
          String(email).toLowerCase().includes(filtreNorm) ||
          (firstNameFromEmail(email) || '').toLowerCase().includes(filtreNorm) ||
          list.some((b) => String(b.nom).toLowerCase().includes(filtreNorm)))
      : parAgent
    return l.slice().sort((a, b) => rang(a) - rang(b))
  }, [parAgent, conflitsParAgent, filtreNorm, ctx])

  const menu = useMemo(() => ({
    ouvertId: menuId,
    // Recliquer sur le même bloc REFERME le menu — une bascule, pas un
    // empilement de menus qu'il faudrait refermer ailleurs.
    basculer: (id) => setMenuId((courant) => (courant === id ? null : id)),
  }), [menuId])

  const gestesAvecFermeture = useMemo(() => ({
    ...gestes,
    onChoisirAgent: (bloc, email) => { setMenuId(null); gestes.onChoisirAgent(bloc, email) },
  }), [gestes])

  // LES DEUX BASCULES SE DISTINGUENT (cadrage définitif 2026-08-22) : celle
  // de la FRISE ne change PAS d'écran — c'est une PASTILLE discrète (fond
  // #eef2f6, rayon 20, 32 px, actif blanc + ombre), posée sur la ligne de la
  // recherche. La bascule de PAGE, elle, est la barre à trait navy.
  const onglets = (
    <div data-testid="frise-onglet" role="tablist"
      style={{display:'inline-flex',background:'#eef2f6',borderRadius:20,padding:3,gap:2}}>
      {[{ id: 'logement', label: 'Par logement', Icone: Home },
        { id: 'agent', label: 'Par agent', Icone: User }].map(({ id, label, Icone }) => {
        const estActif = vue === id
        return (
          <button key={id} type="button" role="tab" aria-selected={estActif}
            data-testid={`frise-onglet-${id}`}
            onClick={() => { if (!estActif) onVue(id) }} className="fbz-press fbz-cible-tactile"
            style={{display:'inline-flex',alignItems:'center',gap:5,height:32,
              padding:'0 12px',borderRadius:17,border:'none',
              background: estActif ? '#fff' : 'none',
              boxShadow: estActif ? '0 1px 4px rgba(35,50,64,.16)' : 'none',
              color: estActif ? '#233240' : '#5c6773',fontSize:11.5,
              fontWeight: estActif ? 700 : 500,fontFamily:DS.fontBody,
              cursor: estActif ? 'default' : 'pointer',whiteSpace:'nowrap'}}>
            <Icone size={13} strokeWidth={2} color={estActif ? '#3d5166' : '#98a2ad'} />
            {label}
          </button>
        )
      })}
    </div>
  )

  return (
    <section data-testid="frise-journee"
      style={{background:'#fff',border:`1px solid ${C.bord}`,borderRadius:12,overflow:'hidden'}}>
      <style>{'[data-testid^="frise-bloc-"]:hover .fbz-poignee-barre{opacity:1 !important}'}</style>
      <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',
        borderBottom:`1px solid ${C.bord}`,flexWrap:'wrap'}}>
        {onglets}
        <label className="fbz-hitbox44" style={{display:'inline-flex',alignItems:'center',gap:5,background:C.colonne,
          border:`1px solid ${C.bord}`,borderRadius:8,padding:'5px 9px',marginLeft:'auto'}}>
          <Search size={13} strokeWidth={2} color={C.muet} />
          <input value={filtre} onChange={(e) => setFiltre(e.target.value)}
            placeholder={vue === 'agent' ? 'un agent…' : 'un logement…'}
            aria-label="Filtrer" data-testid="frise-filtre"
            style={{border:'none',background:'transparent',outline:'none',fontSize:11.5,
              fontFamily:DS.fontBody,color:C.fonce,width:104}} />
        </label>
      </div>

      {ctx.message && (
        <div data-testid="frise-message"
          style={{padding:'9px 13px',background:C.blocGris,color:DS.textSecondary,fontSize:11.5,
            borderBottom:`1px solid ${C.bord}`,lineHeight:1.5}}>
          {ctx.message}
        </div>
      )}

      <div className="fbz-frise-scroll" ref={scrollerRef} style={{padding:`10px ${MARGE_LISTE}px 12px`}}>
        <style>{CSS_FRISE}</style>
        <div className="fbz-frise-piste">
        <EnTeteAxe />

        <div style={{position:'relative'}}>
          {/* TÉLÉPHONE : le trait global est victime d'une anomalie de
              compositing (il peint au-dessus des cellules sticky alors que le
              hit-test les donne devant — mesuré). Sur étroit le trait vit DANS
              chaque piste ; l'ordinateur garde l'overlay au pixel. */}
          {!etroitFrise && (
            <div style={{position:'absolute',left:LARGEUR_GAUCHE,right:0,top:0,bottom:0,
              pointerEvents:'none',zIndex:7}}>
              <TraitMaintenant nowMin={ctx.nowMin} visible={ctx.positionJour === 'aujourdhui'} />
            </div>
          )}

          {vue === 'logement' ? (
            lignes.length === 0 ? <VideFrise filtre={!!filtreNorm} /> : (
              <div style={{ marginTop: 10 }}>
                {/* LES GROUPES ONT DISPARU (Dimitri 2026-08-16) : une ligne ne
                    saute plus jamais sous les yeux — l'état se dit par la
                    PASTILLE et la COULEUR du bloc, l'urgence par le bandeau. */}
                {lignes.map((bloc, bi) => (
                  <div key={bloc.id}
                    style={bi < lignes.length - 1
                      ? { borderBottom: '1px solid #eef1f4' } : undefined}>
                    <LigneLogement bloc={bloc} ctx={ctx}
                      enConflit={conflits.has(bloc.id)} conflitsParAgent={conflitsParAgent}
                      ouvert={ouvertId === bloc.id}
                      onOuvrir={(id) => { setMenuId(null); setOuvertId((c) => (c === id ? null : id)) }}
                      gestes={gestesAvecFermeture} menu={menu} />
                  </div>
                ))}
              </div>
            )
          ) : (
            agentsOrdonnes.length === 0 ? <VideFrise filtre={!!filtreNorm} /> : (
              agentsOrdonnes.map(([email, list]) => (
                <CouloirAgent key={email} email={email} blocs={list} ctx={ctx}
                  conflits={conflits} conflitsParAgent={conflitsParAgent}
                  gestes={gestesAvecFermeture} menu={menu}
                  viserCouloir={viserCouloir} visee={visee} />
              ))
            )
          )}
        </div>
        </div>
      </div>

      {messageConflit && etroitFrise && (
        <button type="button" data-testid="frise-conflit-bloc" className="fbz-press"
          onClick={() => setConflitsOuverts(true)}
          style={{display:'flex',alignItems:'center',gap:8,width:'calc(100% - 20px)',
            margin:'0 10px 10px',background:'#fdf4f0',border:'1px solid #e0b5a5',
            borderRadius:11,padding:'9px 11px',cursor:'pointer',textAlign:'left',
            fontFamily:DS.fontBody}}>
          <AlertTriangle size={15} strokeWidth={2.2} color="#b8412b" style={{flexShrink:0}} />
          <span style={{flex:1,minWidth:0,fontSize:11,color:'#8f2f1d',fontWeight:700,
            overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
            {messageConflit}
          </span>
          <ChevronLegende size={14} strokeWidth={2.2} color="#c99a8a" style={{flexShrink:0}} />
        </button>
      )}
      {conflitsOuverts && etroitFrise && (
        <FeuilleConflits conflits={messageConflit} libres={messageLibres}
          onFermer={() => setConflitsOuverts(false)} />
      )}
      {messageConflit && !etroitFrise && (
        <div data-testid="frise-message-conflit"
          style={{padding:'9px 13px',background:C.fondAlerte,borderTop:`1px solid ${C.bord}`,
            display:'flex',alignItems:'flex-start',gap:8}}>
          <AlertTriangle size={14} strokeWidth={2.2} color={C.texteAlerte}
            style={{flexShrink:0,marginTop:1}} />
          <span style={{fontSize:11.5,lineHeight:1.5}}>
            <span style={{color:C.texteAlerte,fontWeight:700}}>{messageConflit}</span>
            {messageLibres && (
              <span data-testid="frise-agents-libres"
                style={{display:'block',color:C.gris,fontWeight:400,marginTop:2}}>
                {messageLibres}
              </span>
            )}
          </span>
        </div>
      )}
      <LegendeFrise />
    </section>
  )
}

function VideFrise({ filtre }) {
  return (
    <div style={{padding:22,textAlign:'center',color:C.muet,fontSize:12.5}}>
      {filtre ? 'Rien ne correspond au filtre.' : 'Aucun ménage ce jour-là.'}
    </div>
  )
}

// §6 (spec mobile) — LA FEUILLE DES CONFLITS : les conflits écrits, et les
// agents libres en dessous (fond #f4faf7, texte #124a33).
function FeuilleConflits({ conflits, libres, onFermer }) {
  useVoileEcran()
  useEchapPourFermer(onFermer)
  return (<PortailFeuille><>
    <div onClick={onFermer}
      style={{position:'fixed',inset:0,background:'rgba(35,50,64,.35)',zIndex:998}} />
    <div data-testid="feuille-conflits"
      style={{position:'fixed',left:0,right:0,bottom:0,zIndex:999,background:'#fff',
        borderRadius:'18px 18px 0 0',boxShadow:'0 -8px 30px rgba(35,50,64,.22)',
        maxHeight:'78vh',overflowY:'auto',
        padding:'6px 14px calc(14px + env(safe-area-inset-bottom))',fontFamily:DS.fontBody}}>
      <div aria-hidden="true" style={{width:36,height:4,borderRadius:2,
        background:'#d5e3ee',margin:'4px auto 8px'}} />
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        <span style={{flex:1,fontSize:14.5,fontWeight:700,color:'#233240'}}>Les conflits du jour</span>
        <button type="button" aria-label="Fermer" onClick={onFermer} className="fbz-press"
          style={{width:32,height:32,borderRadius:9,border:'none',background:'#f4f6f8',
            color:'#5c6773',display:'inline-flex',alignItems:'center',justifyContent:'center',
            cursor:'pointer'}}>
          <X size={15} strokeWidth={2.2} />
        </button>
      </div>
      <div style={{marginTop:10,background:'#fdf4f0',border:'1px solid #e0b5a5',
        borderRadius:11,padding:'10px 12px',fontSize:12,color:'#8f2f1d',
        fontWeight:700,lineHeight:1.5}}>
        {conflits}
      </div>
      {libres && (
        <div data-testid="feuille-conflits-libres"
          style={{marginTop:8,background:'#f4faf7',borderRadius:11,
            padding:'10px 12px',fontSize:12,color:'#124a33',lineHeight:1.5}}>
          {libres}
        </div>
      )}
    </div>
  </></PortailFeuille>)
}

export { C as COULEURS_FRISE, LARGEUR_GAUCHE }
